import { useState, useEffect } from "react";
import { supabase, signIn, signUp, signOut as supabaseSignOut, getProfile, upsertProfile, getPicks, trackPick, untrackPick, getLeaderboard, getSubscription, getUserStats, getTrackedPicks } from './supabase'

const G = "#22C55E";
const GLt = "#E8F9EE";
const BG = "#ffffff";
const NAVY = "#0D1B2A";
const AMBER = "#F59E0B";
const F  = "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', sans-serif"
const FH = "Georgia, 'Times New Roman', serif";

const TIER_ORDER = ["scout","elite"];

// Convert "Today · HH:MM" UTC time string to local timezone
function localiseTime(timeStr, kickoff) {
  // Always prefer the ISO kickoff field — converts to user's local timezone automatically
  if (kickoff) {
    const dt = new Date(kickoff);
    const now = new Date();
    const isToday = dt.toDateString() === now.toDateString();
    const tomorrow = new Date(now); tomorrow.setDate(now.getDate()+1);
    const isTomorrow = dt.toDateString() === tomorrow.toDateString();
    const localH = dt.getHours().toString().padStart(2,"0");
    const localM = dt.getMinutes().toString().padStart(2,"0");
    const prefix = isToday ? "Today" : isTomorrow ? "Tomorrow" : dt.toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"});
    return `${prefix} · ${localH}:${localM}`;
  }
  // Fallback: treat stored time string as UTC and convert to local
  if (!timeStr || !timeStr.includes("·")) return timeStr;
  const parts = timeStr.split("· ");
  if (parts.length < 2) return timeStr;
  const prefix = parts[0].trim();
  const utcTime = parts[1].trim();
  const [h, m] = utcTime.split(":").map(Number);
  if (isNaN(h) || isNaN(m)) return timeStr;
  const now = new Date();
  const base = prefix === "Tomorrow"
    ? new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()+1, h, m))
    : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), h, m));
  const localH = base.getHours().toString().padStart(2,"0");
  const localM = base.getMinutes().toString().padStart(2,"0");
  return `${prefix} · ${localH}:${localM}`;
}

function getPickStatus(p) {
  // Trust real DB status field first (set by update-scores function)
  if (p.status === "live") return "live";
  if (p.result === "won" || p.result === "lost" || p.result === "draw") return "finished";
  if (p.status === "finished") return "finished";
  return "upcoming";
}

// Sort picks: live first, then upcoming by kickoff asc, finished last
function sortPicks(picks) {
  const order = { live: 0, upcoming: 1, finished: 2 };
  return [...picks].sort((a, b) => {
    const sa = order[getPickStatus(a)];
    const sb = order[getPickStatus(b)];
    if (sa !== sb) return sa - sb;
    // Within same status, sort by kickoff time ascending
    const ta = a.kickoff ? new Date(a.kickoff).getTime() : 0;
    const tb = b.kickoff ? new Date(b.kickoff).getTime() : 0;
    return ta - tb;
  });
}


const tierGte = (user, req) => TIER_ORDER.indexOf(user) >= TIER_ORDER.indexOf(req);

const TIERS = [
  { id:"scout", name:"Scout", price:"Free", desc:"Always free — no card needed",
    highlights:["6 predictions per week","Football & Basketball only","Win / loss prediction","Confidence score"] },
  { id:"elite", name:"Elite", price:"$3.99", desc:"Full access, cancel anytime", popular:true,
    highlights:["Unlimited daily predictions","All 5 sports covered","Full probability breakdown","Expected value (EV) display","Sharp money indicators","AI match analysis","Line movement tracking","Kelly Criterion sizing","Arbitrage scanner","Odds comparison (15+ bookmakers)","6-hour early access","Monte Carlo model detail","Dedicated account manager","WhatsApp & push alerts","Monthly ROI report","Priority support"] },
];

// ── League-specific images ──────────────────────────────────
const LEAGUE_PHOTOS = {
  // Football – European leagues
  "Premier League":          "https://images.unsplash.com/photo-1522778119026-d647f0596c20?w=800&q=80",
  "La Liga":                 "https://images.unsplash.com/photo-1556816213-354f4f83b86a?w=800&q=80",
  "Bundesliga":              "https://images.unsplash.com/photo-1489944440615-453fc2b6a9a9?w=800&q=80",
  "Serie A":                 "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&q=80",
  "Ligue 1":                 "https://images.unsplash.com/photo-1551854838-212c9a5fc607?w=800&q=80",
  "UEFA Champions League":   "https://images.unsplash.com/photo-1518604666860-9ed391f76460?w=800&q=80",
  "UEFA Europa League":      "https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=800&q=80",
  "UEFA Conference League":  "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=800&q=80",
  // Basketball
  "NBA":                     "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800&q=80",
  "EuroLeague":              "https://images.unsplash.com/photo-1504450758481-7338eba7524a?w=800&q=80",
  // Tennis
  "ATP":                     "https://images.unsplash.com/photo-1595435934249-5df7ed86e1c0?w=800&q=80",
  "WTA":                     "https://images.unsplash.com/photo-1542144582-1ba00456b5e3?w=800&q=80",
  "ATP Tour":                "https://images.unsplash.com/photo-1595435934249-5df7ed86e1c0?w=800&q=80",
  "WTA Tour":                "https://images.unsplash.com/photo-1542144582-1ba00456b5e3?w=800&q=80",
  // Am. Football
  "NFL":                     "https://images.unsplash.com/photo-1560272564-c83b66b1ad12?w=800&q=80",
  // Cricket
  "IPL":                     "https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=800&q=80",
  "Test Cricket":            "https://images.unsplash.com/photo-1540747913346-19212a4b423a?w=800&q=80",
};

// Sport fallbacks
const SPORT_PHOTOS = {
  "Football":     "https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=800&q=80",
  "Basketball":   "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800&q=80",
  "Am. Football": "https://images.unsplash.com/photo-1560272564-c83b66b1ad12?w=800&q=80",
  "Tennis":       "https://images.unsplash.com/photo-1595435934249-5df7ed86e1c0?w=800&q=80",
  "Cricket":      "https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=800&q=80",
};

const getPickImage = (pick) => {
  const league = (pick?.league || '').toLowerCase();
  const sport  = pick?.sport || 'Football';
  // Fuzzy league matching — handles API name variations
  if (league.includes('premier league') || league.includes('english premier'))
    return LEAGUE_PHOTOS["Premier League"];
  if (league.includes('la liga') || league.includes('spain'))
    return LEAGUE_PHOTOS["La Liga"];
  if (league.includes('bundesliga') || league.includes('germany'))
    return LEAGUE_PHOTOS["Bundesliga"];
  if (league.includes('serie a') || league.includes('italy'))
    return LEAGUE_PHOTOS["Serie A"];
  if (league.includes('ligue 1') || league.includes('france'))
    return LEAGUE_PHOTOS["Ligue 1"];
  if (league.includes('champions league') || league.includes('ucl'))
    return LEAGUE_PHOTOS["UEFA Champions League"];
  if (league.includes('europa league'))
    return LEAGUE_PHOTOS["UEFA Europa League"];
  if (league.includes('conference league'))
    return LEAGUE_PHOTOS["UEFA Conference League"];
  if (league.includes('nba'))
    return LEAGUE_PHOTOS["NBA"];
  if (league.includes('euroleague') || league.includes('euro league'))
    return LEAGUE_PHOTOS["EuroLeague"];
  if (league.includes('atp'))
    return LEAGUE_PHOTOS["ATP"];
  if (league.includes('wta'))
    return LEAGUE_PHOTOS["WTA"];
  if (league.includes('nfl'))
    return LEAGUE_PHOTOS["NFL"];
  if (league.includes('ipl'))
    return LEAGUE_PHOTOS["IPL"];
  // Fall back to sport
  return SPORT_PHOTOS[sport] || SPORT_PHOTOS["Football"];
};

const PICKS = [] // Picks are managed via /admin panel

const _UNUSED_VALUE_BETS = [
  { id:1, sport:"Football",     match:"Dortmund vs Leipzig", market:"Over 2.5 Goals", bookOdds:"1.78", trueOdds:"1.62", ev:"+9.9%",  kelly:"4.2%", confidence:73 },
  { id:2, sport:"Basketball",   match:"Bucks vs Pacers",     market:"Bucks -5.5",     bookOdds:"2.05", trueOdds:"1.85", ev:"+10.8%", kelly:"5.1%", confidence:68 },
  { id:3, sport:"Am. Football", match:"Cowboys vs Eagles",   market:"Under 46.5",     bookOdds:"1.95", trueOdds:"1.76", ev:"+10.8%", kelly:"4.8%", confidence:71 },
];

const _UNUSED_ARBS = [
  { id:1, sport:"Football",   match:"Bayern vs Leverkusen", bookA:"Bet365",     bookB:"William Hill", profit:"3.2%", stakeA:512, stakeB:488, expires:"4h 12m" },
  { id:2, sport:"Tennis",     match:"Nadal vs Medvedev",    bookA:"Betway",     bookB:"Unibet",        profit:"2.1%", stakeA:476, stakeB:524, expires:"1h 47m" },
  { id:3, sport:"Basketball", match:"Lakers vs Clippers",   bookA:"DraftKings", bookB:"FanDuel",       profit:"1.8%", stakeA:503, stakeB:497, expires:"6h 30m" },
];

const _UNUSED_LEADERBOARD = [
  { rank:1, name:"SharpMike",   accuracy:91, tracked:189, streak:14, tier:"Elite" },
  { rank:2, name:"ValueHunter", accuracy:88, tracked:214, streak:9,  tier:"Elite" },
  { rank:3, name:"QuantTrader", accuracy:85, tracked:156, streak:7,  tier:"Elite" },
  { rank:4, name:"EdgeFinder",  accuracy:83, tracked:201, streak:5,  tier:"Elite" },
  { rank:5, name:"BetLabsAI",   accuracy:79, tracked:143, streak:3,  tier:"Scout" },
  { rank:6, name:"You",         accuracy:74, tracked:54,  streak:2,  tier:"Scout", isUser:true },
  { rank:7, name:"PronosticX",  accuracy:71, tracked:118, streak:0,  tier:"Scout" },
  { rank:8, name:"OddsEngine",  accuracy:68, tracked:92,  streak:1,  tier:"Scout" },
];

const _UNUSED_HISTORY = [
  { match:"Arsenal vs PSG",       result:"WON",  stake:200, pnl:"+$220", date:"Today"     },
  { match:"Celtics vs Heat",       result:"LOST", stake:150, pnl:"-$150", date:"Yesterday" },
  { match:"Chiefs vs Ravens",      result:"WON",  stake:300, pnl:"+$261", date:"Mon"       },
  { match:"Djokovic vs Sinner",    result:"OPEN", stake:100, pnl:"–",     date:"Tomorrow"  },
  { match:"Liverpool vs Man City", result:"WON",  stake:250, pnl:"+$600", date:"Sun"       },
];

const SPORTS_ALL = ["All","Football","Basketball","Am. Football","Tennis","Cricket"];

// ── Icons ──────────────────────────────────────────────────
const Icon = ({ name, size=22, color="#6B7280" }) => {
  const p = {
    home:     <><path d="M3 12L12 3l9 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/><path d="M9 21V12h6v9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/><path d="M3 12v9h18v-9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></>,
    picks:    <><circle cx="12" cy="12" r="9" stroke={color} strokeWidth="2" fill="none"/><path d="M12 8v4l3 3" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none"/></>,
    stats:    <><rect x="3" y="12" width="4" height="9" rx="1" fill={color} opacity="0.5"/><rect x="10" y="7" width="4" height="14" rx="1" fill={color}/><rect x="17" y="3" width="4" height="18" rx="1" fill={color} opacity="0.7"/></>,
    bankroll: <><rect x="2" y="7" width="20" height="14" rx="2" stroke={color} strokeWidth="2" fill="none"/><path d="M16 14a2 2 0 11-4 0 2 2 0 014 0z" fill={color}/><path d="M2 11h20" stroke={color} strokeWidth="2"/></>,
    profile:  <><circle cx="12" cy="8" r="4" stroke={color} strokeWidth="2" fill="none"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none"/></>,
    search:   <><circle cx="11" cy="11" r="7" stroke={color} strokeWidth="2" fill="none"/><path d="M16 16l4 4" stroke={color} strokeWidth="2" strokeLinecap="round"/></>,
    bell:     <><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round"/><path d="M13.73 21a2 2 0 01-3.46 0" stroke={color} strokeWidth="2" fill="none"/></>,
    back:     <><path d="M19 12H5M12 5l-7 7 7 7" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></>,
    share:    <><circle cx="18" cy="5" r="3" stroke={color} strokeWidth="2" fill="none"/><circle cx="6" cy="12" r="3" stroke={color} strokeWidth="2" fill="none"/><circle cx="18" cy="19" r="3" stroke={color} strokeWidth="2" fill="none"/><path d="M8.59 13.51l6.83 3.98M15.41 6.51L8.59 10.49" stroke={color} strokeWidth="2" fill="none"/></>,
    check:    <><path d="M20 6L9 17l-5-5" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/></>,
    lock:     <><rect x="5" y="11" width="14" height="10" rx="2" stroke={color} strokeWidth="2" fill="none"/><path d="M8 11V7a4 4 0 018 0v4" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{display:"block",flexShrink:0}}>
      {p[name]}
    </svg>
  );
};


// ── Logo ───────────────────────────────────────────────────
const LogoMark = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    <path d="M10 40 C10 40 21 18 40 18 C59 18 70 40 70 40 C70 40 59 62 40 62 C21 62 10 40 10 40Z" fill={G} fillOpacity="0.15"/>
    <path d="M10 40 C10 40 21 18 40 18 C59 18 70 40 70 40 C70 40 59 62 40 62 C21 62 10 40 10 40Z" stroke={G} strokeWidth="2.5" fill="none"/>
    <circle cx="40" cy="40" r="12" stroke={G} strokeWidth="2" fill={G} fillOpacity="0.2"/>
    <rect x="34" y="44" width="3" height="5" rx="1" fill={G}/>
    <rect x="39" y="40" width="3" height="9" rx="1" fill={G}/>
    <rect x="44" y="36" width="3" height="13" rx="1" fill={G}/>
    <circle cx="40" cy="40" r="3.5" fill={G}/>
  </svg>
);

const LogoFull = ({ dark = false }) => (
  <div style={{display:"flex",alignItems:"center",gap:10}}>
    <LogoMark size={36}/>
    <div>
      <div style={{display:"flex",alignItems:"baseline",gap:0,lineHeight:1}}>
        <span style={{fontSize:20,fontWeight:700,color:dark?"#fff":NAVY,fontFamily:FH,letterSpacing:"-0.3px"}}>Sport</span>
        <span style={{fontSize:20,fontWeight:700,color:G,fontFamily:FH,letterSpacing:"-0.3px"}}>Oracle</span>
      </div>
      <div style={{fontSize:8,fontWeight:700,color:dark?"rgba(255,255,255,0.4)":"#9CA3AF",letterSpacing:"0.18em",fontFamily:F,marginTop:1}}>PREDICT · TRACK · WIN</div>
    </div>
  </div>
);

// ── Primitives ─────────────────────────────────────────────
const Card = ({ children, style, dark, onClick }) => (
  <div onClick={onClick} style={{
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
    cursor: onClick ? "pointer" : "auto",
    ...style,
  }}>
    {children}
  </div>
);

const GreenBtn = ({ label, onPress, style, small }) => (
  <button onClick={onPress} style={{
    width:"100%", padding: small ? "10px 0" : "16px 0", borderRadius:14,
    border:"none", background:G,
    color:NAVY, fontSize: small ? 13 : 15, fontWeight:800,
    cursor:"pointer", fontFamily:F,
    ...style,
  }}>
    {label}
  </button>
);

const OutlineBtn = ({ label, onPress }) => (
  <button onClick={onPress} style={{
    width:"100%", padding:"14px 0", borderRadius:14,
    border:"1.5px solid #E2E8E4", background:"#fff",
    color:NAVY, fontSize:14, fontWeight:700,
    cursor:"pointer", fontFamily:F,
  }}>
    {label}
  </button>
);

const BADGE_STYLES = {
  "AI TOP PICK":     { color:"#fff",      bg:"#16A34A" },
  "VALUE BET":       { color:"#fff",      bg:"#2563EB" },
  "SHARP PLAY":      { color:NAVY,        bg:AMBER     },
  "HIGH CONFIDENCE": { color:"#fff",      bg:"#7C3AED" },
  "FORM PICK":       { color:NAVY,        bg:G         },
  "AVOID":           { color:"#fff",      bg:"#DC2626" },
};
const Pill = ({ label, color, bg }) => {
  const s = BADGE_STYLES[label] || {};
  return (
    <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.07em",color:color||s.color||NAVY,background:bg||s.bg||G,borderRadius:20,padding:"4px 10px",fontFamily:F,whiteSpace:"nowrap"}}>
      {label}
    </span>
  );
};

const Divider = ({ inset, dark }) => (
  <div style={{height:1,background:dark?"#1E3024":"#F0F2F0",margin:inset?"0 18px":"0"}}/>
);

const Row = ({ label, value, last, dark }) => (
  <div>
    <div style={{display:"flex",justifyContent:"space-between",padding:"13px 18px"}}>
      <span style={{fontSize:14,color:dark?"#9CA3AF":"#6B7280",fontFamily:F}}>{label}</span>
      <span style={{fontSize:14,fontWeight:600,color:dark?"#fff":NAVY,fontFamily:F}}>{value}</span>
    </div>
    {!last && <Divider inset dark={dark}/>}
  </div>
);

const ConfBar = ({ value }) => {
  const color = value >= 70 ? G : value >= 60 ? "#3B82F6" : AMBER;
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
        <span style={{fontSize:11,color:"#6B7280",fontFamily:F,textTransform:"uppercase",letterSpacing:"0.04em"}}>Confidence</span>
        <span style={{fontSize:12,fontWeight:700,color,fontFamily:F}}>{value}%</span>
      </div>
      <div style={{height:6,background:"#E9EEF0",borderRadius:99}}>
        <div style={{height:"100%",width:`${value}%`,background:color,borderRadius:99}}/>
      </div>
    </div>
  );
};

const PageHeader = ({ title, onBack, right }) => (
  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 18px",background:"#fff",borderBottom:"1px solid #F0F2F0"}}>
    <button onClick={onBack} style={{width:38,height:38,borderRadius:12,background:BG,border:"none",cursor:onBack?"pointer":"default",display:"flex",alignItems:"center",justifyContent:"center",opacity:onBack?1:0,pointerEvents:onBack?"auto":"none"}}>
      <Icon name="back" size={18} color={NAVY}/>
    </button>
    <span style={{fontSize:17,fontWeight:700,color:NAVY,fontFamily:F}}>{title}</span>
    <div style={{width:38}}>{right}</div>
  </div>
);

// ── Onboarding ─────────────────────────────────────────────
const OnboardingScreen = ({ onDone }) => {
  const [slide, setSlide] = useState(0);
  const slides = [
    { title:"Professional Picks.", sub:"Powered by AI.", body:"Unlock precision betting insights with our advanced algorithms designed for serious bettors.", badge:"ELITE ACCURACY" },
    { title:"Find Value.", sub:"Before the market.", body:"We scan 15+ bookmakers in real time to surface positive expected value bets with confidence scores.", badge:"REAL-TIME DATA" },
    { title:"Track Your Edge.", sub:"Win consistently.", body:"Kelly Criterion sizing, portfolio ROI, and a leaderboard of the best predictors on the platform.", badge:"PROVEN RESULTS" },
  ];
  const s = slides[slide];
  return (
    <div style={{minHeight:"100vh",background:BG,display:"flex",flexDirection:"column"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 20px"}}>
        <LogoFull/>
        <span onClick={onDone} style={{fontSize:15,color:"#6B7280",fontFamily:F,cursor:"pointer",fontWeight:500}}>Skip</span>
      </div>
      <div style={{margin:"0 16px",borderRadius:20,overflow:"hidden",position:"relative",height:300,background:"#C8DDD5",flexShrink:0}}>
        <div style={{position:"absolute",inset:0,background:"linear-gradient(to bottom,rgba(13,27,42,0) 40%,rgba(13,27,42,0.5) 100%)"}}/>
        <div style={{position:"absolute",top:14,right:14}}><Pill label={s.badge}/></div>
        <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{width:100,height:100,borderRadius:"50%",background:"rgba(34,197,94,0.2)",border:"3px solid rgba(34,197,94,0.4)",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{width:64,height:64,borderRadius:"50%",background:"rgba(34,197,94,0.3)",border:"2px solid "+G,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Icon name="stats" size={28} color={G}/>
            </div>
          </div>
        </div>
      </div>
      <div style={{padding:"28px 24px 0",flex:1}}>
        <h1 style={{fontSize:30,fontWeight:800,color:NAVY,fontFamily:F,margin:"0 0 2px",lineHeight:1.2}}>
          {s.title}<br/><span style={{color:G}}>{s.sub}</span>
        </h1>
        <p style={{fontSize:15,color:"#6B7280",fontFamily:F,lineHeight:1.7,margin:"14px 0 0"}}>{s.body}</p>
      </div>
      <div style={{padding:"24px 20px 36px"}}>
        <div style={{display:"flex",gap:6,justifyContent:"center",marginBottom:24}}>
          {slides.map((_,i) => (
            <div key={i} style={{width:i===slide?28:8,height:8,borderRadius:99,background:i===slide?G:"#C8D5CE",transition:"width 0.3s"}}/>
          ))}
        </div>
        {slide < slides.length-1
          ? <GreenBtn label="Continue →" onPress={() => setSlide(slide+1)}/>
          : <GreenBtn label="Get Started →" onPress={onDone}/>
        }
        <p style={{fontSize:12,color:"#9CA3AF",textAlign:"center",fontFamily:F,marginTop:14,lineHeight:1.6}}>By continuing you agree to our Terms. Please bet responsibly.</p>
      </div>
    </div>
  );
};

// ── Auth ───────────────────────────────────────────────────
const AuthScreen = ({ onAuth }) => {
  const [mode, setMode]       = useState("login"); // login | signup
  const [email, setEmail]     = useState("");
  const [password, setPassword] = useState("");
  const [name, setName]       = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const handleSubmit = async () => {
    setError(""); setLoading(true);
    try {
      if (mode === "signup") {
        const { error: e } = await signUp(email, password, name);
        if (e) { setError(e.message); setLoading(false); return; }
        setError("Check your email to confirm your account, then log in.");
        setMode("login"); setLoading(false); return;
      } else {
        const { data, error: e } = await signIn(email, password);
        if (e) { setError(e.message); setLoading(false); return; }
        if (data?.user) onAuth(data.user);
      }
    } catch(e) { setError("Something went wrong. Please try again."); }
    setLoading(false);
  };

  return (
    <div style={{minHeight:"100vh",background:BG,padding:"0 20px",overflowY:"auto"}}>
      <div style={{padding:"48px 0 28px",display:"flex",justifyContent:"center"}}>
        <LogoFull/>
      </div>
      <div style={{width:64,height:64,borderRadius:16,background:GLt,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:20}}>
        <Icon name="profile" size={32} color={G}/>
      </div>
      <h2 style={{fontSize:28,fontWeight:800,color:NAVY,fontFamily:FH,margin:"0 0 6px"}}>
        {mode === "login" ? "Welcome back" : "Create account"}
      </h2>
      <p style={{fontSize:15,color:"#6B7280",fontFamily:F,margin:"0 0 28px",lineHeight:1.6}}>
        {mode === "login" ? "Sign in to your SportOracle account" : "Join thousands of winning predictors"}
      </p>

      {mode === "signup" && (
        <div style={{marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:NAVY,letterSpacing:"0.08em",fontFamily:F,marginBottom:8}}>FULL NAME</div>
          <input value={name} onChange={e=>setName(e.target.value)}
            placeholder="Your full name" autoComplete="off"
            style={{width:"100%",background:"#fff",borderRadius:14,padding:"16px",fontSize:15,color:NAVY,fontFamily:F,border:"1.5px solid #E5E7EB",outline:"none",boxSizing:"border-box"}}/>
        </div>
      )}

      <div style={{marginBottom:14}}>
        <div style={{fontSize:11,fontWeight:700,color:NAVY,letterSpacing:"0.08em",fontFamily:F,marginBottom:8}}>EMAIL</div>
        <input value={email} onChange={e=>setEmail(e.target.value)}
          placeholder="your@email.com" type="email" inputMode="email" autoComplete="email"
          style={{width:"100%",background:"#fff",borderRadius:14,padding:"16px",fontSize:15,color:NAVY,fontFamily:F,border:"1.5px solid #E5E7EB",outline:"none",boxSizing:"border-box"}}/>
      </div>

      <div style={{marginBottom:22}}>
        <div style={{fontSize:11,fontWeight:700,color:NAVY,letterSpacing:"0.08em",fontFamily:F,marginBottom:8}}>PASSWORD</div>
        <input value={password} onChange={e=>setPassword(e.target.value)}
          placeholder="Min 6 characters" type="password"
          style={{width:"100%",background:"#fff",borderRadius:14,padding:"16px",fontSize:15,color:NAVY,fontFamily:F,border:"1.5px solid #E5E7EB",outline:"none",boxSizing:"border-box"}}/>
      </div>

      {error && (
        <div style={{background: error.includes("Check your") ? GLt : "#FEF2F2",
          border: "1px solid " + (error.includes("Check your") ? G : "#FECACA"),
          borderRadius:12,padding:"12px 16px",marginBottom:16,
          fontSize:13,color: error.includes("Check your") ? "#166534" : "#DC2626",fontFamily:F,lineHeight:1.5}}>
          {error}
        </div>
      )}

      <div onClick={!loading ? handleSubmit : undefined}
        style={{background:loading?"#9CA3AF":G,borderRadius:14,padding:"17px",textAlign:"center",cursor:loading?"not-allowed":"pointer",marginBottom:14,transition:"background 0.2s"}}>
        <span style={{fontSize:16,fontWeight:700,color:"#fff",fontFamily:F}}>
          {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
        </span>
      </div>

      <p style={{fontSize:14,color:"#6B7280",textAlign:"center",fontFamily:F,marginBottom:28}}>
        {mode === "login" ? "Don't have an account? " : "Already have an account? "}
        <span onClick={()=>{setMode(mode==="login"?"signup":"login");setError("");}}
          style={{color:G,fontWeight:700,cursor:"pointer"}}>
          {mode === "login" ? "Sign up free" : "Sign in"}
        </span>
      </p>

      <p style={{fontSize:11,color:"#9CA3AF",textAlign:"center",fontFamily:F,lineHeight:1.6,paddingBottom:40}}>
        By continuing you agree to our{" "}
        <span style={{color:G,fontWeight:600}}>Terms of Service</span> and{" "}
        <span style={{color:G,fontWeight:600}}>Privacy Policy</span>.{" "}
        Please bet responsibly.
      </p>
    </div>
  );
};



// ── Notifications Panel ─────────────────────────────────────
const NotificationsPanel = ({ onClose, picks, trackedIds, user }) => {
  const tracked = picks ? picks.filter(p => trackedIds && trackedIds.has(p.id)) : [];
  const newPicks = picks ? picks.slice(0, 3) : [];
  const now = new Date();

  const notifications = [
    // New picks added today
    ...newPicks.map(p => ({
      id: "pick_" + p.id,
      type: "pick",
      icon: "🎯",
      title: "New pick added",
      body: `${p.home} vs ${p.away} — ${p.prediction} @ ${p.odds}`,
      time: p.time || "Today",
      color: G,
      bg: GLt,
    })),
    // Tracked picks with results
    ...tracked.filter(p => p.result && p.result !== "open").map(p => ({
      id: "result_" + p.id,
      type: "result",
      icon: p.result === "won" ? "✅" : "❌",
      title: p.result === "won" ? "Pick won!" : "Pick lost",
      body: `${p.home} vs ${p.away} — ${p.prediction}`,
      time: "Settled",
      color: p.result === "won" ? G : "#EF4444",
      bg: p.result === "won" ? GLt : "#FEF2F2",
    })),
    // System notification
    {
      id: "sys_1",
      type: "system",
      icon: "⚡",
      title: "Picks update twice daily",
      body: "New predictions are added every morning at 7AM and afternoon at 1PM UTC.",
      time: "Always",
      color: AMBER,
      bg: "#FFF7ED",
    },
  ];

  const isEmpty = newPicks.length === 0 && tracked.filter(p=>p.result).length === 0;

  return (
    <div style={{position:"fixed",inset:0,zIndex:1000}} onClick={onClose}>
      {/* Backdrop */}
      <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.3)"}}/>
      {/* Panel */}
      <div onClick={e=>e.stopPropagation()} style={{
        position:"absolute",top:0,right:0,width:"min(100vw, 380px)",height:"100vh",
        background:"#fff",boxShadow:"-4px 0 24px rgba(0,0,0,0.12)",
        display:"flex",flexDirection:"column",
      }}>
        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"20px 18px 16px",borderBottom:"1px solid #F0F2F0"}}>
          <div>
            <div style={{fontSize:18,fontWeight:800,color:NAVY,fontFamily:F}}>Notifications</div>
            {notifications.length > 0 && <div style={{fontSize:12,color:"#9CA3AF",fontFamily:F,marginTop:2}}>{notifications.length} updates</div>}
          </div>
          <button onClick={onClose} style={{width:34,height:34,borderRadius:10,border:"none",background:"#F3F4F6",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>✕</button>
        </div>

        {/* Content */}
        <div style={{flex:1,overflowY:"auto",padding:"12px 0"}}>
          {isEmpty ? (
            <div style={{textAlign:"center",padding:"60px 24px"}}>
              <div style={{fontSize:40,marginBottom:12}}>🔔</div>
              <div style={{fontSize:15,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:8}}>No notifications yet</div>
              <div style={{fontSize:13,color:"#9CA3AF",fontFamily:F,lineHeight:1.6}}>
                Track picks from the Feed and we'll notify you here when results come in and new picks are added.
              </div>
            </div>
          ) : (
            notifications.map((n, i) => (
              <div key={n.id} style={{
                display:"flex",gap:12,padding:"14px 18px",
                borderBottom: i < notifications.length-1 ? "1px solid #F9FAFB" : "none",
                alignItems:"flex-start",
              }}>
                <div style={{
                  width:40,height:40,borderRadius:12,background:n.bg,
                  display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:18,flexShrink:0,
                }}>{n.icon}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:3}}>
                    <div style={{fontSize:13,fontWeight:700,color:NAVY,fontFamily:F}}>{n.title}</div>
                    <div style={{fontSize:11,color:"#9CA3AF",fontFamily:F,marginLeft:8,flexShrink:0,whiteSpace:"nowrap"}}>{n.time}</div>
                  </div>
                  <div style={{fontSize:12,color:"#6B7280",fontFamily:F,lineHeight:1.5}}>{n.body}</div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {!user && (
          <div style={{padding:"16px 18px",borderTop:"1px solid #F0F2F0",background:"#F9FAFB"}}>
            <div style={{fontSize:12,color:"#9CA3AF",fontFamily:F,textAlign:"center",lineHeight:1.5}}>
              Sign in to get personalised notifications for your tracked picks.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Feed ───────────────────────────────────────────────────

const TrackButton = ({ pickId, tracked, onTrack }) => {
  const [hovered, setHovered] = useState(false);
  const label = tracked ? (hovered ? "✕ Untrack" : "✓ Tracked") : "Track";
  const bg = tracked ? (hovered ? "#ef4444" : G) : "#fff";
  const color = tracked ? "#fff" : G;
  const border = tracked ? (hovered ? "#ef4444" : G) : G;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onTrack && onTrack(pickId); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{padding:"0 16px",borderRadius:14,border:"1.5px solid "+border,background:bg,color,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F,flexShrink:0,transition:"all 0.15s",minWidth:80}}
    >{label}</button>
  );
};

const FeedScreen = ({ tier, onSelectPick, onUpgrade, picks: livePicks, loading, onTrack, trackedIds, onNotif, unreadCount }) => {
  const [sport, setSport] = useState("All");
  const isScout = tier === "scout";
  const sportTabs = SPORTS_ALL; // All tiers see all 5 sport tabs
  const allPicks = sortPicks((livePicks || PICKS).filter(p => getPickStatus(p) !== "finished"));
  let picks = sport === "All" ? allPicks : allPicks.filter(p => p.sport === sport);
  const visiblePicks = [...picks].sort((a,b) => (a.badge==='AVOID'?1:0) - (b.badge==='AVOID'?1:0));
  if (loading) return (
    <div style={{paddingBottom:80}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 18px 14px"}}>
        <LogoFull/>
      </div>
      {[1,2,3].map(i => (
        <div key={i} style={{margin:"0 18px 14px",borderRadius:18,background:"#F3F4F6",height:160,
          animation:"pulse 1.5s ease-in-out infinite"}}/>
      ))}
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}`}</style>
    </div>
  );

  return (
    <div style={{paddingBottom:80}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 18px 14px"}}>
        <LogoFull/>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {isScout && <span style={{fontSize:11,fontWeight:700,color:AMBER,background:"#FFF7ED",borderRadius:8,padding:"4px 8px",fontFamily:F}}>FREE</span>}
          <div onClick={onNotif} style={{width:36,height:36,borderRadius:12,background:"#F3F4F6",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",position:"relative"}}>
            <Icon name="bell" size={18} color={NAVY}/>
            {unreadCount > 0 && (
              <div style={{position:"absolute",top:-4,right:-4,width:16,height:16,borderRadius:"50%",background:"#EF4444",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <span style={{fontSize:9,fontWeight:800,color:"#fff",fontFamily:F}}>{unreadCount > 9 ? "9+" : unreadCount}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {isScout && (
        <div style={{margin:"0 18px 14px",background:GLt,borderRadius:12,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:13,color:NAVY,fontFamily:F,fontWeight:500}}>Upgrade to Elite for unlimited picks & all sports</span>
          <span onClick={onUpgrade} style={{fontSize:13,fontWeight:800,color:G,fontFamily:F,cursor:"pointer"}}>Upgrade →</span>
        </div>
      )}

      <div style={{display:"flex",overflowX:"auto",paddingLeft:18,paddingBottom:14,scrollbarWidth:"none"}}>
        {sportTabs.map(s => (
          <button key={s} onClick={()=>setSport(s)} style={{
            flexShrink:0,padding:"8px 16px",background:"none",border:"none",
            borderBottom:"2px solid "+(sport===s?G:"transparent"),
            color:sport===s?G:"#6B7280",fontSize:14,fontWeight:sport===s?700:500,
            cursor:"pointer",fontFamily:F,marginRight:4,
          }}>{s}</button>
        ))}
      </div>

      <div style={{padding:"0 18px"}}>
        {visiblePicks.length === 0 && (
        <div style={{textAlign:"center",padding:"60px 24px",color:"#9CA3AF"}}>
          <div style={{fontSize:40,marginBottom:12}}>🔍</div>
          <div style={{fontSize:16,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:6}}>No picks yet</div>
          <div style={{fontSize:13,fontFamily:F}}>Check back soon — our analysts are working on today's picks.</div>
        </div>
      )}
      {visiblePicks.map(p => {
          const scoutSportLocked = isScout && p.sport !== "Football" && p.sport !== "Basketball";
          const locked = !tierGte(tier, p.minTier) || scoutSportLocked;
          const isAvoid = p.badge === 'AVOID'
          return (
            <Card key={p.id} style={{marginBottom:16,overflow:"hidden",opacity:isAvoid?0.65:1,filter:isAvoid?"grayscale(60%)":"none"}} onClick={() => locked ? onUpgrade() : onSelectPick(p)}>
              <div style={{position:"relative",height:190,background:"#C8DDD5",overflow:"hidden"}}>
                <img src={getPickImage(p)} alt={p.sport} style={{width:"100%",height:"100%",objectFit:"cover",transition:"transform 0.3s"}} onError={e=>{e.target.src="https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=800&q=80"}}/>
                <div style={{position:"absolute",inset:0,background:isAvoid?"linear-gradient(to bottom,rgba(0,0,0,0.2) 0%,rgba(0,0,0,0.3) 40%,rgba(13,27,42,0.9) 100%)":"linear-gradient(to bottom,rgba(0,0,0,0.05) 0%,rgba(0,0,0,0.15) 40%,rgba(13,27,42,0.82) 100%)"}}/>
                {isAvoid && (
                  <div style={{position:"absolute",top:0,left:0,right:0,background:"rgba(220,38,38,0.85)",padding:"5px 12px",display:"flex",alignItems:"center",gap:6,zIndex:2}}>
                    <span style={{fontSize:10,fontWeight:800,color:"#fff",fontFamily:F,letterSpacing:"0.08em"}}>⚠ LOW VALUE — MARKET DISAGREES WITH THIS PICK</span>
                  </div>
                )}
                <div style={{position:"absolute",top:12,left:12}}>
                  {locked
                    ? <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.07em",color:"#fff",background:"rgba(0,0,0,0.55)",borderRadius:20,padding:"4px 10px",fontFamily:F}}>🔒 Elite only</span>
                    : <Pill label={p.badge}/>
                  }
                </div>
                {p.early && tierGte(tier,"elite") && (
                  <div style={{position:"absolute",top:12,right:12}}><Pill label="6H EARLY" color={NAVY} bg={AMBER}/></div>
                )}
                {p.sharp && tierGte(tier,"elite") && (
                  <div style={{position:"absolute",top:p.early?44:12,right:12}}><Pill label="⚡ SHARP" color="#fff" bg="rgba(245,158,11,0.85)"/></div>
                )}
                {/* Team names overlaid on image */}
                <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"12px 14px"}}>
                  <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.7)",letterSpacing:"0.1em",fontFamily:F,marginBottom:4}}>{p.league}</div>
                  <div style={{fontSize:18,fontWeight:800,color:"#fff",fontFamily:F,letterSpacing:"-0.2px",lineHeight:1.2,textShadow:"0 1px 4px rgba(0,0,0,0.4)"}}>{p.home} <span style={{color:"rgba(255,255,255,0.5)",fontWeight:400,fontSize:14}}>vs</span> {p.away}</div>
                  {getPickStatus(p) === "live"
                  ? <div style={{display:"flex",alignItems:"center",gap:6,marginTop:3}}>
                      <div style={{width:7,height:7,borderRadius:"50%",background:"#EF4444",animation:"pulse 1s infinite"}}/>
                      <span style={{fontSize:11,fontWeight:700,color:"#FF6B6B",fontFamily:F}}>IN PROGRESS</span>
                      {p.score && <span style={{fontSize:12,fontWeight:800,color:"#fff",fontFamily:F,background:"rgba(0,0,0,0.4)",padding:"1px 6px",borderRadius:4}}>{p.score}</span>}
                    </div>
                  : <div style={{fontSize:11,color:"rgba(255,255,255,0.65)",fontFamily:F,marginTop:3}}>🕐 {localiseTime(p.time, p.kickoff)}</div>
                }
                </div>
              </div>
              <div style={{padding:"12px 16px"}}>
                <div style={{fontSize:11,fontWeight:700,color:G,letterSpacing:"0.08em",fontFamily:F,marginBottom:5}}>{p.league}</div>
                <div style={{fontSize:17,fontWeight:800,color:NAVY,fontFamily:F,marginBottom:10,letterSpacing:"-0.2px"}}>{p.home} vs {p.away}</div>
                {locked ? (
                  <div style={{background:BG,borderRadius:10,padding:"12px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span style={{fontSize:13,color:"#6B7280",fontFamily:F}}>Upgrade to unlock this pick</span>
                    <span style={{fontSize:13,fontWeight:700,color:G,fontFamily:F}}>Upgrade →</span>
                  </div>
                ) : (
                  <div>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:16}}>
                      {[
                        {label:"PICK",       value:p.prediction, color:isAvoid?"#9CA3AF":NAVY},
                        {label:"CONFIDENCE", value:p.confidence+"%", color:isAvoid?"#9CA3AF":G},
                        {label:tierGte(tier,"elite")?"EV":"ODDS", value:tierGte(tier,"elite")?p.ev:p.odds, color:isAvoid?"#EF4444":G},
                      ].map((m,i) => (
                        <div key={i}>
                          <div style={{fontSize:10,color:"#9CA3AF",fontFamily:F,letterSpacing:"0.06em",marginBottom:4}}>{m.label}</div>
                          <div style={{fontSize:15,fontWeight:700,color:m.color,fontFamily:F}}>{m.value}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <GreenBtn label="View Pick" onPress={()=>onSelectPick(p)} style={{flex:1}}/>
                      <TrackButton pickId={p.id} tracked={trackedIds&&trackedIds.has(p.id)} onTrack={onTrack} />
                    </div>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// ── Pick Detail ────────────────────────────────────────────
const PickDetailScreen = ({ pick, tier, onBack, onUpgrade, onTrack, tracked }) => {
  const [tab, setTab] = useState("Overview");
  if (!pick) return null;

  const tabs = ["Overview", "AI Analysis"];
  if (tierGte(tier,"elite")) tabs.push("Odds");

  const status   = getPickStatus(pick);
  const isLive   = status === "live";
  const isFin    = status === "finished";
  const isWon    = pick.result === "won";
  const isLost   = pick.result === "lost";
  const evNum    = parseFloat(String(pick.ev || '0').replace(/[^0-9.\-]/g, ''));
  const evPos    = !isNaN(evNum) && evNum > 0;
  const isAvoid  = pick.badge === "AVOID";

  // Resolve analysis — handle both JSONB object and legacy string
  // analysis is already resolved in loadPicks mapping
  const analysis = (pick.analysis && typeof pick.analysis === 'object' && (pick.analysis.verdict || pick.analysis.narrative))
    ? pick.analysis : null;
  const analysisString = typeof pick.analysis === 'string' ? pick.analysis : '';

  // Kelly calc
  const kp = pick.probH > 0 ? Math.min(6, Math.max(0,
    ((( (parseFloat(pick.odds)-1) * pick.probH/100) - (1 - pick.probH/100)) / (parseFloat(pick.odds)-1)) * 100
  )) : 0;
  const bankroll = (() => {
    try { return JSON.parse(localStorage.getItem("so_bankroll") || "null")?.current || 1000; } catch { return 1000; }
  })();

  const BADGE_COLORS = {
    "AI TOP PICK":     { bg:"#DCFCE7", color:"#15803D" },
    "VALUE BET":       { bg:"#DBEAFE", color:"#1D4ED8" },
    "SHARP PLAY":      { bg:"#FEF3C7", color:"#B45309" },
    "FORM PICK":       { bg:"#F0FDF4", color:"#16A34A" },
    "HIGH CONFIDENCE": { bg:"#EDE9FE", color:"#7C3AED" },
    "AVOID":           { bg:"#FEE2E2", color:"#DC2626" },
  };
  const bc = BADGE_COLORS[pick.badge] || BADGE_COLORS["FORM PICK"];

  return (
    <div style={{background:"#F8FAFC",minHeight:"100vh",paddingBottom:100}}>
      {/* Sticky header */}
      <div style={{background:"#fff",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 16px",borderBottom:"1px solid #F0F2F0",position:"sticky",top:0,zIndex:10}}>
        <button onClick={onBack} style={{width:36,height:36,borderRadius:10,background:"#F3F4F6",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <Icon name="back" size={16} color={NAVY}/>
        </button>
        <div style={{textAlign:"center",flex:1,padding:"0 12px"}}>
          <div style={{fontSize:14,fontWeight:700,color:NAVY,fontFamily:F,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{pick.home} vs {pick.away}</div>
          <div style={{fontSize:11,color:"#9CA3AF",fontFamily:F,marginTop:1}}>{pick.league}</div>
        </div>
        <button
          onClick={()=>onTrack(pick.id)}
          style={{
            width:36,height:36,borderRadius:10,border:"none",cursor:"pointer",
            background: tracked ? GLt : "#F3F4F6",
            display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:18
          }}
        >{tracked ? "★" : "☆"}</button>
      </div>

      {/* Hero image */}
      <div style={{position:"relative",height:180,overflow:"hidden"}}>
        <img src={getPickImage(pick)} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}} onError={e=>{e.target.src="https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=800&q=80"}}/>
        <div style={{position:"absolute",inset:0,background:"linear-gradient(to bottom,rgba(0,0,0,0) 0%,rgba(13,27,42,0.9) 100%)"}}/>
        <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"14px 16px",display:"flex",justifyContent:"space-between",alignItems:"flex-end"}}>
          <div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.55)",fontFamily:F,marginBottom:4}}>{pick.league}</div>
            <div style={{fontSize:20,fontWeight:800,color:"#fff",fontFamily:F,lineHeight:1.2}}>{pick.home} <span style={{color:"rgba(255,255,255,0.4)",fontWeight:400,fontSize:14}}>vs</span> {pick.away}</div>
            {isLive
              ? <div style={{display:"flex",alignItems:"center",gap:5,marginTop:5}}>
                  <div style={{width:6,height:6,borderRadius:"50%",background:"#EF4444"}}/>
                  <span style={{fontSize:11,fontWeight:700,color:"#FF6B6B",fontFamily:F}}>LIVE</span>
                  {pick.score && <span style={{fontSize:12,fontWeight:800,color:"#fff",fontFamily:F,background:"rgba(0,0,0,0.5)",padding:"2px 8px",borderRadius:4}}>{pick.score}</span>}
                </div>
              : <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",fontFamily:F,marginTop:4}}>🕐 {localiseTime(pick.time, pick.kickoff)}</div>
            }
          </div>
          <div style={{background:bc.bg,borderRadius:8,padding:"5px 10px"}}>
            <span style={{fontSize:11,fontWeight:800,color:bc.color,fontFamily:F,letterSpacing:"0.03em"}}>{pick.badge}</span>
          </div>
        </div>
      </div>

      {/* Result banner if settled */}
      {(isWon || isLost) && (
        <div style={{
          background: isWon ? G : "#EF4444",
          padding:"10px 16px",
          display:"flex",alignItems:"center",justifyContent:"center",gap:8
        }}>
          <span style={{fontSize:14,fontWeight:800,color:"#fff",fontFamily:F}}>
            {isWon ? "✓ WON" : "✗ LOST"} — {pick.prediction}
          </span>
          {pick.score && <span style={{fontSize:12,color:"rgba(255,255,255,0.8)",fontFamily:F}}>{pick.score}</span>}
        </div>
      )}

      <div style={{padding:"14px 14px 0"}}>
        {/* Primary prediction card */}
        <div style={{background:"#fff",borderRadius:16,padding:"18px",marginBottom:12,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:10,fontWeight:700,color:G,letterSpacing:"0.1em",fontFamily:F,marginBottom:8}}>AI PREDICTION</div>
          <div style={{fontSize:22,fontWeight:800,color:NAVY,fontFamily:F,marginBottom:14}}>{pick.prediction}</div>

          {/* 3 key metrics */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom: tierGte(tier,"elite") ? 14 : 0}}>
            {[
              {label:"Confidence", value:pick.confidence+"%", color:G},
              {label:"Best Odds",  value:pick.odds,           color:NAVY},
              {label:"Exp. Value", value:pick.ev,             color:evPos?G:"#EF4444"},
            ].map((m,i) => (
              <div key={i} style={{background:"#F8FAFC",borderRadius:10,padding:"10px 8px",textAlign:"center"}}>
                <div style={{fontSize:18,fontWeight:800,color:m.color,fontFamily:F,lineHeight:1}}>{m.value}</div>
                <div style={{fontSize:10,color:"#9CA3AF",fontFamily:F,marginTop:5}}>{m.label}</div>
              </div>
            ))}
          </div>

          {/* Probability bar — Elite only */}
          {tierGte(tier,"elite") && pick.probH > 0 && (
            <div>
              <div style={{display:"flex",gap:2,height:7,borderRadius:99,overflow:"hidden",marginBottom:6}}>
                <div style={{flex:pick.probH,background:G}}/>
                {pick.sport==="Football" && <div style={{flex:pick.probD,background:"#D1D5DB"}}/>}
                <div style={{flex:pick.probA,background:"#9CA3AF"}}/>
              </div>
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <span style={{fontSize:11,fontWeight:600,color:G,fontFamily:F}}>H {pick.probH}%</span>
                {pick.sport==="Football" && <span style={{fontSize:11,color:"#9CA3AF",fontFamily:F}}>D {pick.probD}%</span>}
                <span style={{fontSize:11,color:"#9CA3AF",fontFamily:F}}>A {pick.probA}%</span>
              </div>
            </div>
          )}

          {!tierGte(tier,"elite") && (
            <div style={{background:"#F8FAFC",borderRadius:8,padding:"9px 12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:12,color:"#6B7280",fontFamily:F}}>Probability breakdown</span>
              <span onClick={onUpgrade} style={{fontSize:12,fontWeight:700,color:G,fontFamily:F,cursor:"pointer"}}>Elite →</span>
            </div>
          )}
        </div>

        {/* Sharp indicator */}
        {pick.sharp && tierGte(tier,"elite") && (
          <div style={{background:"#FFF7ED",borderRadius:12,padding:"11px 14px",marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:16}}>⚡</span>
            <span style={{fontSize:12,color:AMBER,fontFamily:F,fontWeight:600,lineHeight:1.5}}>Sharp money detected — professional bettors are backing {pick.prediction}</span>
          </div>
        )}

        {/* Kelly stake — Elite only */}
        {tierGte(tier,"elite") && kp > 0.5 && (
          <div style={{background:"#fff",borderRadius:12,padding:"13px 16px",marginBottom:12,boxShadow:"0 1px 4px rgba(0,0,0,0.06)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.07em",fontFamily:F,marginBottom:4}}>KELLY CRITERION STAKE</div>
              <div style={{fontSize:22,fontWeight:800,color:AMBER,fontFamily:F}}>{kp.toFixed(1)}% <span style={{fontSize:13,color:"#9CA3AF",fontWeight:400}}>of bankroll</span></div>
            </div>
            <div style={{background:"#F8FAFC",borderRadius:10,padding:"8px 14px",textAlign:"center"}}>
              <div style={{fontSize:11,color:"#9CA3AF",fontFamily:F}}>On €{bankroll.toLocaleString()}</div>
              <div style={{fontSize:16,fontWeight:800,color:NAVY,fontFamily:F}}>€{(bankroll * kp / 100).toFixed(0)}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{display:"flex",background:"#fff",borderRadius:12,padding:3,marginBottom:12,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
          {tabs.map(t => (
            <button key={t} onClick={()=>setTab(t)} style={{
              flex:1,padding:"9px 4px",borderRadius:9,border:"none",
              background:tab===t?G:"transparent",
              color:tab===t?"#fff":"#6B7280",
              fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F,transition:"all 0.15s"
            }}>{t}</button>
          ))}
        </div>

        {/* ── OVERVIEW TAB ── */}
        {tab==="Overview" && (
          <div style={{background:"#fff",borderRadius:14,overflow:"hidden",boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
            {[
              {l:"Sport",        v:pick.sport},
              {l:"League",       v:pick.league},
              {l:"Kickoff",      v:localiseTime(pick.time, pick.kickoff)},
              {l:"Prediction",   v:pick.prediction},
              {l:"Confidence",   v:pick.confidence+"%"},
              {l:"Expected Value",v:pick.ev},
              {l:"Best Odds",    v:pick.odds},
              {l:"Badge",        v:pick.badge},
              {l:"Risk Level",   v:pick.riskLevel||"Medium"},
            ].map((r,i,arr) => (
              <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",borderBottom:i<arr.length-1?"1px solid #F3F4F6":"none"}}>
                <span style={{fontSize:13,color:"#6B7280",fontFamily:F}}>{r.l}</span>
                <span style={{fontSize:13,fontWeight:600,color:NAVY,fontFamily:F}}>{r.v}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── AI ANALYSIS TAB ── */}
        {tab==="AI Analysis" && (
          <div>
            {analysis ? (
              <>
                {/* Verdict */}
                {analysis.verdict && (
                  <div style={{background:NAVY,borderRadius:14,padding:"18px",marginBottom:12}}>
                    <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.4)",letterSpacing:"0.1em",fontFamily:F,marginBottom:8}}>VERDICT</div>
                    <div style={{fontSize:16,fontWeight:700,color:"#fff",fontFamily:F,lineHeight:1.5}}>{analysis.verdict}</div>
                  </div>
                )}

                {/* Narrative — teaser for scout, full for elite */}
                {analysis.narrative && (() => {
                  const sentences = analysis.narrative.split(/(?<=[.!?])\s+/);
                  const teaser = sentences.slice(0,2).join(' ');
                  const rest = sentences.slice(2).join(' ');
                  return (
                    <div style={{background:"#fff",borderRadius:14,padding:"18px",marginBottom:12,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                      <div style={{fontSize:10,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.1em",fontFamily:F,marginBottom:10}}>MATCH ANALYSIS</div>
                      <p style={{fontSize:14,color:NAVY,fontFamily:F,lineHeight:1.85,margin:0}}>{teaser}</p>
                      {tierGte(tier,"elite") ? (
                        rest && <p style={{fontSize:14,color:NAVY,fontFamily:F,lineHeight:1.85,margin:"12px 0 0"}}>{rest}</p>
                      ) : (
                        rest && (
                          <div style={{position:"relative",marginTop:12}}>
                            <p style={{fontSize:14,color:NAVY,fontFamily:F,lineHeight:1.85,margin:0,filter:"blur(4px)",userSelect:"none",pointerEvents:"none"}}>{rest}</p>
                            <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8}}>
                              <div style={{background:"rgba(255,255,255,0.95)",borderRadius:12,padding:"12px 18px",textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.12)"}}>
                                <div style={{fontSize:13,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:4}}>Full analysis — Elite</div>
                                <span onClick={onUpgrade} style={{fontSize:12,fontWeight:700,color:G,fontFamily:F,cursor:"pointer"}}>Unlock for $3.99/mo →</span>
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  );
                })()}

                {/* Key Factors — Elite only */}
                {tierGte(tier,"elite") && analysis.keyFactors && analysis.keyFactors.length > 0 && (
                  <div style={{background:"#fff",borderRadius:14,padding:"18px",marginBottom:12,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                    <div style={{fontSize:10,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.1em",fontFamily:F,marginBottom:12}}>KEY FACTORS</div>
                    {analysis.keyFactors.map((f,i) => (
                      <div key={i} style={{display:"flex",gap:10,marginBottom:i<analysis.keyFactors.length-1?10:0,alignItems:"flex-start"}}>
                        <div style={{width:20,height:20,borderRadius:6,background:GLt,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
                          <span style={{fontSize:10,fontWeight:800,color:G}}>↑</span>
                        </div>
                        <span style={{fontSize:13,color:NAVY,fontFamily:F,lineHeight:1.6}}>{f}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Model Data — Elite only */}
                {tierGte(tier,"elite") && analysis.modelData && (
                  <div style={{background:"#fff",borderRadius:14,padding:"18px",marginBottom:12,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                    <div style={{fontSize:10,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.1em",fontFamily:F,marginBottom:12}}>MODEL DATA</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                      {[
                        {l:"Our Probability",  v:(analysis.modelData.homeWinProb||pick.probH)+"%"},
                        {l:"Market Implied",   v:(analysis.modelData.impliedProb||Math.round(100/parseFloat(pick.odds||1.9)))+"%"},
                        {l:"Model Edge",       v:analysis.modelData.modelEdge||pick.ev},
                        {l:"Confidence Band",  v:analysis.modelData.confidenceBand||pick.confidence+"%"},
                      ].map((m,i) => (
                        <div key={i} style={{background:"#F8FAFC",borderRadius:10,padding:"10px 12px"}}>
                          <div style={{fontSize:10,color:"#9CA3AF",fontFamily:F,marginBottom:4}}>{m.l}</div>
                          <div style={{fontSize:15,fontWeight:800,color:NAVY,fontFamily:F}}>{m.v}</div>
                        </div>
                      ))}
                    </div>
                    {analysis.modelData.riskFlags && analysis.modelData.riskFlags.length > 0 && (
                      <div>
                        <div style={{fontSize:10,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.08em",fontFamily:F,marginBottom:8}}>RISK FLAGS</div>
                        {analysis.modelData.riskFlags.map((flag, i) => (
                          <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:i<analysis.modelData.riskFlags.length-1?6:0}}>
                            <span style={{color:"#F59E0B",fontSize:12,flexShrink:0}}>⚠</span>
                            <span style={{fontSize:12,color:"#6B7280",fontFamily:F,lineHeight:1.5}}>{flag}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : analysisString ? (
              // Legacy string analysis
              <div style={{background:"#fff",borderRadius:14,padding:"18px",marginBottom:12,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                <div style={{fontSize:10,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.1em",fontFamily:F,marginBottom:10}}>AI MATCH ANALYSIS</div>
                <p style={{fontSize:14,color:NAVY,fontFamily:F,lineHeight:1.85,margin:0}}>{analysisString}</p>
              </div>
            ) : (
              // No analysis stored — show fallback
              <div style={{background:"#fff",borderRadius:14,padding:"24px 18px",textAlign:"center",boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                <div style={{fontSize:32,marginBottom:10}}>🤖</div>
                <div style={{fontSize:14,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:6}}>Analysis available from tomorrow</div>
                <div style={{fontSize:12,color:"#9CA3AF",fontFamily:F,lineHeight:1.7}}>
                  Deep AI analysis is generated and stored for all new picks. This pick was generated before rich analysis was added — new picks will show the full breakdown.
                </div>
                {/* Fallback computed summary */}
                <div style={{background:"#F8FAFC",borderRadius:10,padding:"14px",marginTop:14,textAlign:"left"}}>
                  <p style={{fontSize:13,color:NAVY,fontFamily:F,lineHeight:1.8,margin:0}}>
                    Our model assigns {pick.probH}% win probability to {pick.home} based on league position, recent form, and home advantage. The bookmaker's implied probability of {Math.round(100/parseFloat(pick.odds||1.9))}% diverges from our estimate, generating {pick.ev} expected value. Confidence level: {pick.confidence}%.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── ODDS TAB ── */}
        {tab==="Odds" && (() => {
          const comp = pick.oddsComp || [];
          const getOddsVal = (item) => {
            const pred = pick.prediction || '';
            const key = pred.includes('Home') ? 'h' : pred.includes('Away') ? 'a' : 'd';
            return parseFloat(item[key] || item.h || 0);
          };
          const best = comp.length > 0 ? comp.reduce((a,b) => getOddsVal(b) > getOddsVal(a) ? b : a, comp[0]) : null;
          return (
            <div>
              {best && (
                <div style={{background:G,borderRadius:14,padding:"16px 18px",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.65)",letterSpacing:"0.1em",fontFamily:F,marginBottom:4}}>BEST ODDS</div>
                    <div style={{fontSize:28,fontWeight:800,color:"#fff",fontFamily:F}}>{getOddsVal(best).toFixed(2)}</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.7)",fontFamily:F}}>{best.book}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.6)",fontFamily:F,marginBottom:4}}>vs model pick</div>
                    <div style={{fontSize:18,fontWeight:700,color:"#fff",fontFamily:F}}>
                      {getOddsVal(best) > parseFloat(pick.odds) ? "+" : ""}{(getOddsVal(best)-parseFloat(pick.odds)).toFixed(2)}
                    </div>
                  </div>
                </div>
              )}
              {comp.length > 0 ? (
                <div style={{background:"#fff",borderRadius:14,overflow:"hidden",boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                  <div style={{padding:"12px 16px 8px",borderBottom:"1px solid #F3F4F6"}}>
                    <span style={{fontSize:10,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.07em",fontFamily:F}}>ALL BOOKMAKERS</span>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:1,background:"#F3F4F6"}}>
                    {comp.map((item, i) => {
                      const isBest = best && item.book === best.book;
                      const val = getOddsVal(item);
                      return (
                        <div key={i} style={{background: isBest ? GLt : "#fff",padding:"12px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <span style={{fontSize:12,fontWeight:isBest?700:500,color:isBest?G:NAVY,fontFamily:F}}>{item.book}</span>
                          <span style={{fontSize:14,fontWeight:800,color:isBest?G:NAVY,fontFamily:F}}>{val>0?val.toFixed(2):"—"}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div style={{background:"#fff",borderRadius:14,padding:"24px",textAlign:"center",boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                  <div style={{fontSize:13,color:"#9CA3AF",fontFamily:F}}>Odds comparison data not available for this pick.</div>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};
// ── Stats ──────────────────────────────────────────────────
const StatsScreen = ({ tier, onUpgrade, user }) => {
  const [trackedPicks, setTrackedPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    getTrackedPicks(user.id).then(({ data }) => {
      if (data) {
        // getTrackedPicks returns { tracked_picks row + nested picks object }
        const mapped = data.map(t => {
          const p = t.picks || {};
          return {
            ...p,
            trackedAt: t.created_at,
          };
        }).filter(p => p.id);
        setTrackedPicks(mapped);
      }
      setLoading(false);
    });
  }, [user]);

  // ── Derived stats from real tracked picks ──────────────────
  const settled = trackedPicks.filter(p => p.result === 'won' || p.result === 'lost' || p.result === 'draw');
  const won     = settled.filter(p => p.result === 'won').length;
  const lost    = settled.filter(p => p.result === 'lost').length;
  const open    = trackedPicks.filter(p => !p.result || p.result === 'pending').length;
  const total   = won + lost;
  const hitPct  = total > 0 ? Math.round((won / total) * 100) : 0;

  // ROI: sum of (odds - 1) for wins minus 1 per loss, divided by total bets
  const roi = settled.length > 0 ? (() => {
    const profit = settled.reduce((acc, p) => {
      if (p.result === 'won') return acc + (parseFloat(p.odds || '1.90') - 1);
      return acc - 1;
    }, 0);
    return ((profit / settled.length) * 100).toFixed(1);
  })() : null;

  // Current streak
  const streak = (() => {
    const byDate = [...settled].sort((a, b) => new Date(b.kickoff||b.created_at) - new Date(a.kickoff||a.created_at));
    if (byDate.length === 0) return null;
    const first = byDate[0].result;
    let count = 0;
    for (const p of byDate) {
      if (p.result === first) count++;
      else break;
    }
    return { type: first, count };
  })();

  // Best sport by hit rate
  const sportStats = trackedPicks.reduce((acc, p) => {
    const s = p.sport || 'Football';
    if (!acc[s]) acc[s] = { won: 0, lost: 0 };
    if (p.result === 'won') acc[s].won++;
    if (p.result === 'lost') acc[s].lost++;
    return acc;
  }, {});

  // Mini sparkline data: last 10 settled picks (1=won, 0=lost)
  const sparkData = [...settled]
    .sort((a, b) => new Date(a.kickoff||a.created_at) - new Date(b.kickoff||b.created_at))
    .slice(-10)
    .map(p => p.result === 'won' ? 1 : 0);

  const sportEmoji = { Football:'⚽', Basketball:'🏀', Hockey:'🏒', Tennis:'🎾' };

  if (!user) return (
    <div style={{background:"#fff",minHeight:"100vh",paddingBottom:80}}>
      <div style={{padding:"20px 18px 12px"}}>
        <div style={{fontSize:22,fontWeight:800,color:NAVY,fontFamily:F,letterSpacing:"-0.4px"}}>Your Stats</div>
      </div>
      <div style={{margin:"60px 24px",textAlign:"center"}}>
        <div style={{fontSize:48,marginBottom:16}}>📊</div>
        <div style={{fontSize:17,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:8}}>Sign in to track picks</div>
        <div style={{fontSize:13,color:"#6B7280",fontFamily:F,lineHeight:1.7}}>Your win rate, ROI, streaks and sport breakdown all live here once you start tracking.</div>
      </div>
    </div>
  );

  return (
    <div style={{background:"#F8FAFC",minHeight:"100vh",paddingBottom:80}}>
      {/* Header */}
      <div style={{background:"#fff",padding:"20px 18px 0",borderBottom:"1px solid #F0F2F0"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
          <div>
            <div style={{fontSize:22,fontWeight:800,color:NAVY,fontFamily:F,letterSpacing:"-0.4px"}}>Your Stats</div>
            <div style={{fontSize:12,color:"#9CA3AF",fontFamily:F,marginTop:2}}>
              {trackedPicks.length} picks tracked · {settled.length} settled
            </div>
          </div>
          {streak && streak.count >= 2 && (
            <div style={{
              background: streak.type==='won' ? GLt : "#FEF2F2",
              borderRadius:10,padding:"6px 12px",textAlign:"center"
            }}>
              <div style={{fontSize:16,fontWeight:800,color:streak.type==='won'?G:"#EF4444",fontFamily:F}}>{streak.count}{streak.type==='won'?'🔥':'❄️'}</div>
              <div style={{fontSize:9,color:"#6B7280",fontFamily:F,letterSpacing:"0.06em"}}>{streak.type==='won'?'WIN':'LOSE'} STREAK</div>
            </div>
          )}
        </div>
        {/* Tab pills */}
        <div style={{display:"flex",gap:0}}>
          {[["overview","Overview"],["history","History"],["breakdown","By Sport"]].map(([id,label]) => (
            <button key={id} onClick={()=>setActiveTab(id)} style={{
              flex:1,padding:"10px 4px",border:"none",background:"transparent",cursor:"pointer",
              fontSize:13,fontWeight:700,fontFamily:F,
              color:activeTab===id?NAVY:"#9CA3AF",
              borderBottom:activeTab===id?`2px solid ${G}`:"2px solid transparent",
              transition:"all 0.15s"
            }}>{label}</button>
          ))}
        </div>
      </div>

      <div style={{padding:"16px 16px 0"}}>
        {loading ? (
          <div style={{textAlign:"center",padding:"60px 0",color:"#9CA3AF",fontFamily:F}}>Loading your stats…</div>
        ) : activeTab === "overview" ? (
          <>
            {/* Hero metrics row */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:12}}>
              {[
                { label:"WIN RATE", value: total > 0 ? hitPct+"%" : "—", sub:`${won}W · ${lost}L`, color: total>0?(hitPct>=55?G:"#EF4444"):NAVY },
                { label:"ROI", value: roi !== null ? (parseFloat(roi)>=0?"+":"")+roi+"%" : "—", sub:"per settled pick", color: roi!==null?(parseFloat(roi)>=0?G:"#EF4444"):NAVY },
                { label:"TRACKED", value: trackedPicks.length, sub:`${open} pending`, color: NAVY },
              ].map((m,i) => (
                <div key={i} style={{background:"#fff",borderRadius:14,padding:"14px 10px",textAlign:"center",boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                  <div style={{fontSize:9,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.08em",fontFamily:F,marginBottom:6}}>{m.label}</div>
                  <div style={{fontSize:22,fontWeight:800,color:m.color,fontFamily:F,lineHeight:1}}>{m.value}</div>
                  <div style={{fontSize:10,color:"#9CA3AF",fontFamily:F,marginTop:5}}>{m.sub}</div>
                </div>
              ))}
            </div>

            {/* Sparkline — recent form */}
            {sparkData.length >= 3 && (
              <div style={{background:"#fff",borderRadius:14,padding:"14px 16px",marginBottom:12,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                <div style={{fontSize:10,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.07em",fontFamily:F,marginBottom:10}}>RECENT FORM — LAST {sparkData.length} PICKS</div>
                <div style={{display:"flex",gap:4,alignItems:"flex-end",height:32}}>
                  {sparkData.map((v,i) => (
                    <div key={i} style={{
                      flex:1,height:v?28:14,borderRadius:3,
                      background:v?G:"#FCA5A5",
                      transition:"height 0.3s ease"
                    }}/>
                  ))}
                </div>
                <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
                  <span style={{fontSize:10,color:"#9CA3AF",fontFamily:F}}>Oldest</span>
                  <span style={{fontSize:10,color:"#9CA3AF",fontFamily:F}}>Most recent</span>
                </div>
              </div>
            )}

            {/* Win/loss bar */}
            {total > 0 && (
              <div style={{background:"#fff",borderRadius:14,padding:"14px 16px",marginBottom:12,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                <div style={{fontSize:10,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.07em",fontFamily:F,marginBottom:10}}>WIN / LOSS SPLIT</div>
                <div style={{display:"flex",height:10,borderRadius:99,overflow:"hidden",gap:2,marginBottom:8}}>
                  <div style={{flex:won,background:G,minWidth:won>0?4:0}}/>
                  <div style={{flex:lost,background:"#FCA5A5",minWidth:lost>0?4:0}}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <span style={{fontSize:12,fontWeight:700,color:G,fontFamily:F}}>{won} Wins ({hitPct}%)</span>
                  <span style={{fontSize:12,fontWeight:700,color:"#EF4444",fontFamily:F}}>{lost} Losses ({100-hitPct}%)</span>
                </div>
              </div>
            )}

            {trackedPicks.length === 0 && (
              <div style={{background:"#fff",borderRadius:14,padding:"32px 24px",textAlign:"center",boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                <div style={{fontSize:44,marginBottom:12}}>🎯</div>
                <div style={{fontSize:16,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:8}}>No picks tracked yet</div>
                <div style={{fontSize:13,color:"#6B7280",fontFamily:F,lineHeight:1.7}}>
                  Tap "Track" on any pick in the Feed. Your win rate, ROI and streaks build automatically as matches settle.
                </div>
              </div>
            )}

            {!tierGte(tier,"elite") && settled.length > 0 && (
              <div style={{background:"#fff",borderRadius:14,padding:"14px 16px",marginBottom:12,boxShadow:"0 1px 4px rgba(0,0,0,0.06)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:NAVY,fontFamily:F}}>Full analytics — Elite</div>
                  <div style={{fontSize:11,color:"#6B7280",fontFamily:F,marginTop:2}}>ROI by sport, confidence bands, badge performance</div>
                </div>
                <span onClick={onUpgrade} style={{fontSize:13,fontWeight:700,color:G,fontFamily:F,cursor:"pointer",flexShrink:0,marginLeft:12}}>Upgrade →</span>
              </div>
            )}
          </>

        ) : activeTab === "history" ? (
          <>
            {trackedPicks.length === 0 ? (
              <div style={{background:"#fff",borderRadius:14,padding:"32px 24px",textAlign:"center",boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                <div style={{fontSize:13,color:"#6B7280",fontFamily:F}}>No tracked picks yet.</div>
              </div>
            ) : (
              <div style={{background:"#fff",borderRadius:14,overflow:"hidden",boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                {[...trackedPicks]
                  .sort((a,b) => new Date(b.kickoff||b.created_at) - new Date(a.kickoff||a.created_at))
                  .map((p, i, arr) => {
                    const res = p.result;
                    const status = getPickStatus(p);
                    const isWon  = res === 'won';
                    const isLost = res === 'lost';
                    const isPending = !res || res === 'pending';
                    const isLive = status === 'live';
                    return (
                      <div key={p.id || i}>
                        <div style={{display:"flex",alignItems:"center",padding:"13px 16px",gap:12}}>
                          {/* Result indicator */}
                          <div style={{
                            width:8,height:8,borderRadius:"50%",flexShrink:0,
                            background: isWon?G : isLost?"#EF4444" : isLive?"#F59E0B" : "#D1D5DB"
                          }}/>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:13,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                              {p.home} vs {p.away}
                            </div>
                            <div style={{fontSize:11,color:"#6B7280",fontFamily:F}}>
                              {p.prediction} · {p.odds} · {p.league}
                            </div>
                          </div>
                          <div style={{textAlign:"right",flexShrink:0}}>
                            <div style={{
                              fontSize:11,fontWeight:700,fontFamily:F,
                              color: isWon?G : isLost?"#EF4444" : isLive?"#F59E0B" : "#9CA3AF"
                            }}>
                              {isLive ? "🔴 LIVE" : isWon ? "WON" : isLost ? "LOST" : isPending ? "PENDING" : res?.toUpperCase()}
                            </div>
                            {p.score && <div style={{fontSize:10,color:"#9CA3AF",fontFamily:F,marginTop:2}}>{p.score}</div>}
                          </div>
                        </div>
                        {i < arr.length - 1 && <div style={{height:1,background:"#F3F4F6",margin:"0 16px"}}/>}
                      </div>
                    );
                  })}
              </div>
            )}
          </>

        ) : (
          // By Sport tab
          <>
            {Object.keys(sportStats).length === 0 ? (
              <div style={{background:"#fff",borderRadius:14,padding:"32px 24px",textAlign:"center",boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                <div style={{fontSize:13,color:"#6B7280",fontFamily:F}}>Track and settle picks to see sport breakdown.</div>
              </div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {Object.entries(sportStats).map(([sport, st]) => {
                  const t = st.won + st.lost;
                  const pct = t > 0 ? Math.round((st.won / t) * 100) : 0;
                  return (
                    <div key={sport} style={{background:"#fff",borderRadius:14,padding:"16px",boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontSize:20}}>{sportEmoji[sport] || '🏅'}</span>
                          <div>
                            <div style={{fontSize:14,fontWeight:700,color:NAVY,fontFamily:F}}>{sport}</div>
                            <div style={{fontSize:11,color:"#9CA3AF",fontFamily:F}}>{t} settled</div>
                          </div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:20,fontWeight:800,color:pct>=55?G:pct>0?"#EF4444":"#9CA3AF",fontFamily:F}}>{t>0?pct+"%":"—"}</div>
                          <div style={{fontSize:11,color:"#9CA3AF",fontFamily:F}}>{st.won}W · {st.lost}L</div>
                        </div>
                      </div>
                      {t > 0 && (
                        <div style={{display:"flex",height:6,borderRadius:99,overflow:"hidden",gap:2}}>
                          <div style={{flex:st.won,background:G}}/>
                          <div style={{flex:st.lost,background:"#FCA5A5"}}/>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── Value Bets ─────────────────────────────────────────────
const ValueScreen = ({ tier, onBack, onUpgrade, liveValuePicks }) => {
  if (!tierGte(tier,"elite")) return (
    <div>
      <PageHeader title="Value Bets" onBack={onBack}/>
      <div style={{padding:"32px 20px",textAlign:"center"}}>
        <Card style={{padding:"32px 24px"}}>
          <div style={{width:60,height:60,borderRadius:30,background:BG,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
            <Icon name="lock" size={26} color="#9CA3AF"/>
          </div>
          <div style={{fontSize:20,fontWeight:800,color:NAVY,fontFamily:F,marginBottom:10}}>Elite Plan Required</div>
          <div style={{fontSize:14,color:"#6B7280",fontFamily:F,lineHeight:1.7,marginBottom:24}}>Value bet detection is available from the Elite plan upwards.</div>
          <GreenBtn label="Upgrade to Elite — $3.99/mo" onPress={onUpgrade}/>
        </Card>
      </div>
    </div>
  );
  return (
    <div style={{paddingBottom:80}}>
      <PageHeader title="Value Bets" onBack={onBack}/>
      <div style={{padding:"16px 18px"}}>
        <p style={{fontSize:14,color:"#6B7280",fontFamily:F,marginBottom:16,lineHeight:1.6}}>Bets where our model's true probability exceeds the bookmaker's implied odds.</p>
        {!tierGte(tier,"elite") && (
          <div style={{background:GLt,borderRadius:12,padding:"12px 16px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:13,color:NAVY,fontFamily:F}}>Kelly Criterion sizing — Elite plan</span>
            <span onClick={onUpgrade} style={{fontSize:13,fontWeight:700,color:G,fontFamily:F,cursor:"pointer"}}>Upgrade →</span>
          </div>
        )}
        <div style={{background:"#F9FAFB",borderRadius:16,padding:"24px",textAlign:"center",marginBottom:16}}>
          <div style={{fontSize:36,marginBottom:12}}>📡</div>
          <div style={{fontSize:15,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:8}}>Value bets update daily</div>
          <div style={{fontSize:13,color:"#6B7280",fontFamily:F,lineHeight:1.7}}>
            Our AI scans upcoming fixtures each morning and afternoon. Value bets — where our model's probability exceeds the bookmaker's implied odds — appear here automatically once detected.
          </div>
        </div>
        {/* Live value bets derived from today's picks with positive EV */}
        {(liveValuePicks||[]).length > 0 && liveValuePicks.map(v => (
          <Card key={v.id} style={{marginBottom:14}}>
            <div style={{padding:"12px 16px",background:BG,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:G,letterSpacing:"0.07em",fontFamily:F}}>{v.sport?.toUpperCase()}</div>
                <div style={{fontSize:14,fontWeight:700,color:NAVY,fontFamily:F}}>{v.home} vs {v.away}</div>
              </div>
              <div style={{fontSize:18,fontWeight:800,color:G,fontFamily:F}}>{v.ev||"—"}</div>
            </div>
            <div style={{padding:"14px 16px"}}>
              <div style={{fontSize:15,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:14}}>{v.prediction}</div>
              <div style={{display:"grid",gridTemplateColumns:tierGte(tier,"elite")?"repeat(3,1fr)":"repeat(2,1fr)",gap:8,marginBottom:14}}>
                {[
                  {label:"Odds",       value:v.odds,              color:NAVY, show:true},
                  {label:"Confidence", value:(v.confidence||0)+"%", color:G,   show:true},
                  {label:"Kelly",      value:(v.kelly||0).toFixed(1)+"%", color:AMBER, show:tierGte(tier,"elite")},
                ].filter(m=>m.show).map((m,i) => (
                  <div key={i} style={{background:BG,borderRadius:10,padding:"10px 8px",textAlign:"center"}}>
                    <div style={{fontSize:10,color:"#9CA3AF",fontFamily:F,marginBottom:5}}>{m.label}</div>
                    <div style={{fontSize:14,fontWeight:800,color:m.color,fontFamily:F}}>{m.value}</div>
                  </div>
                ))}
              </div>
              <ConfBar value={v.confidence||0}/>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ── Bankroll ───────────────────────────────────────────────
const BankrollScreen = ({ tier, onBack, onUpgrade }) => {
  const stored = (() => { try { return JSON.parse(localStorage.getItem("so_bankroll") || "null"); } catch { return null; } })();
  const [step, setStep]         = useState(stored ? "dashboard" : "setup");
  const [inputStart, setInputStart] = useState(stored ? stored.starting.toString() : "");
  const [inputCurrent, setInputCurrent] = useState(stored ? stored.current.toString() : "");
  const [stakeAmount, setStakeAmount] = useState("");
  const [odds, setOdds]   = useState("1.95");
  const [prob, setProb]   = useState("55");

  const save = () => {
    const s = parseFloat(inputStart);
    if (isNaN(s) || s <= 0) return;
    const cur = parseFloat(inputCurrent);
    const obj = { starting: s, current: isNaN(cur) || cur <= 0 ? s : cur };
    localStorage.setItem("so_bankroll", JSON.stringify(obj));
    if (window._soUserId) { import('./supabase').then(({upsertProfile})=>upsertProfile(window._soUserId,{bankroll:JSON.stringify(obj)})).catch(()=>{}); }
    setStep("dashboard");
  };

  const reset = () => {
    localStorage.removeItem("so_bankroll");
    if (window._soUserId) { import('./supabase').then(({upsertProfile})=>upsertProfile(window._soUserId,{bankroll:null})).catch(()=>{}); }
    setInputStart(""); setInputCurrent("");
    setStep("setup");
  };

  const bl = stored || { starting: 0, current: 0 };
  const pnl    = bl.current - bl.starting;
  const pnlPct = bl.starting > 0 ? (pnl / bl.starting) * 100 : 0;
  const fmt    = (n) => "$" + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 });

  // Kelly
  const calcKelly = () => {
    const o = parseFloat(odds);
    const p = parseFloat(prob) / 100;
    if (isNaN(o) || isNaN(p) || o <= 1 || p <= 0 || p >= 1) return null;
    const b = o - 1;
    const k = ((b * p) - (1 - p)) / b;
    return Math.max(0, k * 100);
  };
  const rawKelly = calcKelly();
  // Cap at 10% max — Kelly is theoretical, real bettors never go above 5-10%
  const kellyPct = rawKelly !== null ? Math.min(rawKelly, 10) : null;
  const bankrollForCalc = bl.current || parseFloat(inputCurrent) || 0;
  const kellyDollar = kellyPct !== null && bankrollForCalc > 0 ? bankrollForCalc * kellyPct / 100 : null;
  const isCapped = rawKelly !== null && rawKelly > 10;

  // Stake input mode
  const stakeNum = parseFloat(stakeAmount);
  const stakeKelly = stakeNum > 0 && bankrollForCalc > 0 ? (stakeNum / bankrollForCalc) * 100 : null;
  const stakeRisk = stakeKelly === null ? null : stakeKelly < 2 ? {label:"CONSERVATIVE",color:"#22C55E",bg:"#F0FDF4"} : stakeKelly < 5 ? {label:"MODERATE",color:AMBER,bg:"#FFFBEB"} : stakeKelly < 10 ? {label:"AGGRESSIVE",color:"#F97316",bg:"#FFF7ED"} : {label:"VERY HIGH RISK",color:"#EF4444",bg:"#FEF2F2"};

  if (step === "setup") return (
    <div style={{paddingBottom:80}}>
      <PageHeader title="Bankroll Manager" onBack={onBack}/>
      <div style={{padding:"24px 18px"}}>
        <div style={{textAlign:"center",marginBottom:28}}>
          <div style={{fontSize:40,marginBottom:12}}>💰</div>
          <div style={{fontSize:20,fontWeight:800,color:NAVY,fontFamily:F,marginBottom:8}}>Set Up Your Bankroll</div>
          <div style={{fontSize:14,color:"#6B7280",fontFamily:F,lineHeight:1.6}}>Enter your betting budget to get personalised stake recommendations and track your performance.</div>
        </div>
        <Card style={{padding:"20px",marginBottom:16}}>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.07em",fontFamily:F,marginBottom:8}}>TOTAL BETTING BUDGET ($)</div>
            <input value={inputStart} onChange={e=>setInputStart(e.target.value)} placeholder="e.g. 500" type="number"
              style={{width:"100%",padding:"14px 16px",borderRadius:12,border:"1.5px solid #E2E8E4",fontSize:18,color:NAVY,fontFamily:F,outline:"none",boxSizing:"border-box",fontWeight:700}}/>
            <div style={{fontSize:12,color:"#9CA3AF",fontFamily:F,marginTop:6}}>This is the total amount you're willing to bet with. Don't use money you can't afford to lose.</div>
          </div>
        </Card>
        <button onClick={save} disabled={!inputStart || isNaN(parseFloat(inputStart)) || parseFloat(inputStart)<=0}
          style={{width:"100%",padding:"16px",borderRadius:14,background:!inputStart||isNaN(parseFloat(inputStart))||parseFloat(inputStart)<=0?"#E5E7EB":G,color:"#fff",fontSize:16,fontWeight:800,cursor:"pointer",border:"none",fontFamily:F}}>
          Get Started →
        </button>
      </div>
    </div>
  );

  return (
    <div style={{paddingBottom:80}}>
      <PageHeader title="Bankroll Manager" onBack={onBack}/>
      <div style={{padding:"16px 18px"}}>

        {/* Summary bar */}
        <Card style={{padding:"20px",marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:14,fontWeight:700,color:NAVY,fontFamily:F}}>Your Bankroll</div>
            <button onClick={reset} style={{fontSize:12,color:"#9CA3AF",background:"none",border:"none",cursor:"pointer",fontFamily:F}}>Edit</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
            {[
              {l:"Starting",  v:fmt(bl.starting), sub:null,              color:NAVY},
              {l:"Current",   v:fmt(bl.current),  sub:null,              color:NAVY},
              {l:"P&L",       v:(pnl>=0?"+":"-")+fmt(pnl), sub:(pnl>=0?"+":"")+pnlPct.toFixed(1)+"%", color:pnl>0?G:pnl<0?"#EF4444":"#9CA3AF"},
            ].map((m,i)=>(
              <div key={i} style={{textAlign:"center",padding:"12px 8px",background:"#F8FAF8",borderRadius:10}}>
                <div style={{fontSize:16,fontWeight:800,color:m.color,fontFamily:F}}>{m.v}</div>
                {m.sub && <div style={{fontSize:11,fontWeight:700,color:m.color,fontFamily:F}}>{m.sub}</div>}
                <div style={{fontSize:10,color:"#9CA3AF",fontFamily:F,marginTop:2}}>{m.l}</div>
              </div>
            ))}
          </div>
          <button onClick={()=>{ const c2=prompt("Enter your current bankroll ($):"); if(c2&&!isNaN(parseFloat(c2))){ const upd={...bl,current:parseFloat(c2)}; localStorage.setItem("so_bankroll",JSON.stringify(upd)); if(window._soUserId){import('./supabase').then(({upsertProfile})=>upsertProfile(window._soUserId,{bankroll:JSON.stringify(upd)})).catch(()=>{})} setInputCurrent(c2); }}} 
            style={{marginTop:12,width:"100%",padding:"10px",borderRadius:10,background:"#F0FDF4",color:G,fontSize:13,fontWeight:700,cursor:"pointer",border:"1.5px solid #BBF7D0",fontFamily:F}}>
            Update Current Bankroll
          </button>
        </Card>

        {/* Stake calculator */}
        <Card style={{padding:"20px",marginBottom:16}}>
          <div style={{fontSize:14,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:4}}>How Much Should I Stake?</div>
          <div style={{fontSize:12,color:"#9CA3AF",fontFamily:F,lineHeight:1.6,marginBottom:16}}>Enter the odds and confidence from a pick to get your recommended stake.</div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.06em",fontFamily:F,marginBottom:6}}>DECIMAL ODDS</div>
              <input value={odds} onChange={e=>setOdds(e.target.value)} placeholder="e.g. 1.95" type="number" step="0.01"
                style={{width:"100%",padding:"12px 14px",borderRadius:10,border:"1.5px solid #E2E8E4",fontSize:15,color:NAVY,fontFamily:F,outline:"none",boxSizing:"border-box"}}/>
            </div>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.06em",fontFamily:F,marginBottom:6}}>CONFIDENCE (%)</div>
              <input value={prob} onChange={e=>setProb(e.target.value)} placeholder="e.g. 72" type="number" min="1" max="99"
                style={{width:"100%",padding:"12px 14px",borderRadius:10,border:"1.5px solid #E2E8E4",fontSize:15,color:NAVY,fontFamily:F,outline:"none",boxSizing:"border-box"}}/>
            </div>
          </div>
          <div style={{fontSize:11,color:"#9CA3AF",fontFamily:F,marginBottom:16}}>💡 Copy the odds and confidence % directly from any pick above.</div>

          {kellyPct === null ? (
            <div style={{padding:"16px",background:"#F8FAF8",borderRadius:12,textAlign:"center",fontSize:13,color:"#9CA3AF",fontFamily:F}}>Enter valid odds and confidence to see recommendation</div>
          ) : rawKelly !== null && rawKelly <= 0 ? (
            <div style={{padding:"16px",background:"#FEF2F2",borderRadius:12}}>
              <div style={{fontSize:14,fontWeight:700,color:"#EF4444",fontFamily:F,marginBottom:6}}>❌ Don't Bet This</div>
              <div style={{fontSize:13,color:"#6B7280",fontFamily:F,lineHeight:1.6,marginBottom:10}}>
                At {prob}% confidence and odds of {odds}, this bet has <strong>negative expected value</strong> — you'd lose money long-term.
              </div>
              <div style={{padding:"10px 12px",background:"#fff",borderRadius:8,fontSize:12,color:"#6B7280",fontFamily:F,lineHeight:1.6}}>
                💡 At odds of <strong>{odds}</strong>, you need at least <strong>{parseFloat(odds)>1?Math.ceil((1/parseFloat(odds))*100):0}% confidence</strong> to make this bet worthwhile. Your current confidence is {prob}%.
              </div>
            </div>
          ) : (
            <div style={{background:"#F8FAF8",borderRadius:12,padding:"16px",border:"1.5px solid #E2E8E4"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div style={{fontSize:12,color:"#6B7280",fontFamily:F}}>Recommended stake (Kelly)</div>
                <span style={{fontSize:11,fontWeight:700,color:kellyPct<3?"#22C55E":kellyPct<6?AMBER:"#F97316",background:kellyPct<3?"#F0FDF4":kellyPct<6?"#FFFBEB":"#FFF7ED",borderRadius:8,padding:"3px 10px",fontFamily:F}}>
                  {kellyPct<3?"CONSERVATIVE":kellyPct<6?"MODERATE":"AGGRESSIVE"}
                </span>
              </div>
              <div style={{fontSize:34,fontWeight:800,color:G,fontFamily:F}}>{fmt(kellyDollar||0)}</div>
              <div style={{fontSize:13,color:"#6B7280",fontFamily:F,marginTop:2,marginBottom:12}}>= {kellyPct.toFixed(1)}% of your {fmt(bankrollForCalc)} bankroll</div>
              {isCapped && (
                <div style={{padding:"10px 12px",background:"#F0FDF4",borderRadius:8,fontSize:12,color:"#166534",fontFamily:F,lineHeight:1.5}}>
                  ✅ Raw Kelly suggested {rawKelly.toFixed(0)}% — capped at 10% to protect your bankroll. Professional bettors rarely exceed 5%.
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Check my stake */}
        {tierGte(tier,"elite") && (
          <Card style={{padding:"20px",marginBottom:16}}>
            <div style={{fontSize:14,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:4}}>Check My Stake</div>
            <div style={{fontSize:12,color:"#9CA3AF",fontFamily:F,marginBottom:12}}>Already decided how much to bet? Check if it's sensible.</div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.06em",fontFamily:F,marginBottom:6}}>STAKE AMOUNT ($)</div>
              <input value={stakeAmount} onChange={e=>setStakeAmount(e.target.value)} placeholder={`e.g. ${bankrollForCalc>0?(bankrollForCalc*0.03).toFixed(0):"25"}`} type="number"
                style={{width:"100%",padding:"12px 14px",borderRadius:10,border:"1.5px solid #E2E8E4",fontSize:15,color:NAVY,fontFamily:F,outline:"none",boxSizing:"border-box"}}/>
            </div>
            {stakeRisk && (
              <div style={{padding:"14px",background:stakeRisk.bg,borderRadius:10,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:stakeRisk.color,fontFamily:F}}>{stakeRisk.label}</div>
                  <div style={{fontSize:12,color:"#6B7280",fontFamily:F,marginTop:2}}>{stakeKelly?.toFixed(1)}% of your bankroll</div>
                </div>
                <div style={{fontSize:11,color:"#6B7280",fontFamily:F,textAlign:"right"}}>
                  {stakeKelly && stakeKelly < 5 ? "✓ Sensible" : stakeKelly && stakeKelly < 10 ? "Proceed with care" : "Consider reducing"}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Rules */}
        <Card style={{padding:"18px"}}>
          <div style={{fontSize:13,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:12}}>Golden Rules</div>
          {[
            {icon:"📌", rule:"Max 5% per bet — no matter how confident you are"},
            {icon:"🔄", rule:"Update your bankroll after every settled bet"},
            {icon:"🛑", rule:"Stop if you lose 20% — review before continuing"},
            {icon:"🧠", rule:"Never chase losses — stick to the system"},
          ].map((r,i)=>(
            <div key={i} style={{display:"flex",gap:10,marginBottom:i<3?10:0,alignItems:"flex-start"}}>
              <span style={{fontSize:16}}>{r.icon}</span>
              <div style={{fontSize:12,color:"#6B7280",fontFamily:F,lineHeight:1.5,paddingTop:1}}>{r.rule}</div>
            </div>
          ))}
        </Card>

      </div>
    </div>
  );
};

// ── Arbitrage ──────────────────────────────────────────────
const ArbScreen = ({ tier, onBack, onUpgrade }) => (
  <div style={{paddingBottom:80}}>
    <PageHeader title="Arbitrage" onBack={onBack}/>
    <div style={{padding:"16px 18px"}}>
      <p style={{fontSize:14,color:"#6B7280",fontFamily:F,marginBottom:16,lineHeight:1.6}}>Guaranteed profit across bookmakers · Elite only</p>
      {!tierGte(tier,"elite") ? (
        <Card style={{padding:"32px 24px",textAlign:"center"}}>
          <div style={{width:60,height:60,borderRadius:30,background:BG,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
            <Icon name="lock" size={26} color="#9CA3AF"/>
          </div>
          <div style={{fontSize:20,fontWeight:800,color:NAVY,fontFamily:F,marginBottom:10}}>Elite Plan Required</div>
          <div style={{fontSize:14,color:"#6B7280",fontFamily:F,lineHeight:1.7,marginBottom:24}}>Scans 15+ bookmakers simultaneously to find guaranteed profit windows before they close.</div>
          <GreenBtn label="Upgrade to Elite — $3.99/mo" onPress={onUpgrade}/>
        </Card>
      ) : (
        <div>
          <div style={{background:"#F9FAFB",borderRadius:16,padding:"32px 24px",textAlign:"center",marginBottom:16}}>
            <div style={{fontSize:40,marginBottom:12}}>⚡</div>
            <div style={{fontSize:16,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:8}}>Arbitrage Scanner</div>
            <div style={{fontSize:13,color:"#6B7280",fontFamily:F,lineHeight:1.7}}>
              Live cross-bookmaker arbitrage detection is launching soon. You'll be among the first to access guaranteed profit windows as an Elite subscriber.
            </div>
          </div>
          {[
            {book1:"Bet365",book2:"Pinnacle",match:"Arsenal vs Chelsea",arb:"2.1%",profit:"$21"},
            {book1:"Betway",book2:"Unibet",match:"Lakers vs Celtics",arb:"1.8%",profit:"$18"},
            {book1:"William Hill",book2:"Bet365",match:"Djokovic vs Alcaraz",arb:"1.4%",profit:"$14"},
          ].map((a,i)=>(
            <div key={i} style={{background:"#fff",border:"1px solid #E5E7EB",borderRadius:12,padding:"14px 16px",marginBottom:10,opacity:0.4,filter:"blur(3px)",userSelect:"none"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{fontSize:13,fontWeight:600,color:NAVY,fontFamily:F}}>{a.match}</div>
                <div style={{fontSize:13,fontWeight:700,color:G,fontFamily:F}}>{a.arb}</div>
              </div>
              <div style={{fontSize:12,color:"#6B7280",fontFamily:F,marginTop:4}}>{a.book1} vs {a.book2} · Est. profit {a.profit}/€1000</div>
            </div>
          ))}
          <div style={{background:GLt,border:"1px solid rgba(34,197,94,0.2)",borderRadius:12,padding:"14px 16px",display:"flex",gap:12,alignItems:"flex-start",marginTop:8}}>
            <div style={{fontSize:13,color:NAVY,fontFamily:F,lineHeight:1.6}}>
              <strong>Launching Q2 2025.</strong> You'll get an in-app notification the moment live scanning goes live.
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
);

// ── Plans + Payment Flow ──────────────────────────────────
const PlansScreen = ({ currentTier, onSelect, onBack }) => {
  const [annual,  setAnnual]  = useState(false);
  const [step,    setStep]    = useState("plans");   // plans | payment | success
  const [chosen,  setChosen]  = useState(null);
  const [card,    setCard]    = useState({ number:"", expiry:"", cvv:"", name:"" });
  const [loading, setLoading] = useState(false);
  const [errors,  setErrors]  = useState({});

  const fmtCard   = v => v.replace(/\D/g,"").slice(0,16).replace(/(.{4})/g,"$1 ").trim();
  const fmtExpiry = v => { const d = v.replace(/\D/g,"").slice(0,4); return d.length>2 ? d.slice(0,2)+"/"+d.slice(2) : d; };
  const fmtCvv    = v => v.replace(/\D/g,"").slice(0,4);

  const validate = () => {
    const e = {};
    if (card.number.replace(/\s/g,"").length < 16) e.number = "Enter a valid 16-digit card number";
    if (card.expiry.length < 5) e.expiry = "Enter expiry as MM/YY";
    if (card.cvv.length < 3)    e.cvv    = "Enter 3 or 4 digit CVV";
    if (!card.name.trim())      e.name   = "Enter cardholder name";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handlePay = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const userId = window._soUserId;
      const email  = card.name ? undefined : undefined; // email comes from auth
      const priceKey = annual ? 'elite_annual' : 'elite_monthly';
      const { createClient } = await import('@supabase/supabase-js');
      const sb = createClient(
        'https://hldacglrddqchkjavfme.supabase.co',
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImhsZGFjZ2xyZGRxY2hramF2Zm1lIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI4Njc5NjIsImV4cCI6MjA4ODQ0Mzk2Mn0.Qs9eAHTqgECJzuVwV-yKrPDqmBNw-i1eqq14lNJzMi4'
      );
      const { data: { session } } = await sb.auth.getSession();
      const userEmail = session?.user?.email ?? '';
      const resp = await fetch('https://hldacglrddqchkjavfme.supabase.co/functions/v1/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (session?.access_token ?? '') },
        body: JSON.stringify({ priceKey, userId, email: userEmail }),
      });
      const { url, error } = await resp.json();
      if (error) throw new Error(error);
      window.location.href = url;
    } catch (err) {
      setLoading(false);
      alert('Payment setup failed: ' + err.message + '\n\nPlease contact support@sportoracle.app');
    }
  };

  const getPrice = (t) => {
    if (t.price === "Free") return "Free";
    const n = parseFloat(t.price.replace("$",""));
    return annual ? "$"+(n*0.8).toFixed(2) : "$"+n.toFixed(2);
  };

  // ── Step 1: Plan list ──
  if (step === "plans") return (
    <div style={{paddingBottom:88}}>
      <PageHeader title="Choose a Plan" onBack={onBack}/>
      <div style={{padding:"20px 18px 0"}}>
        <div style={{marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"center",marginBottom:12}}><LogoFull/></div>
          <p style={{fontSize:14,color:"#6B7280",fontFamily:F,textAlign:"center",margin:0}}>Cancel anytime. No hidden fees.</p>
        </div>
        <div style={{display:"flex",background:"#F3F4F6",borderRadius:12,padding:4,marginBottom:22}}>
          {["Monthly","Annual"].map(m => (
            <button key={m} onClick={()=>setAnnual(m==="Annual")} style={{flex:1,padding:"10px 0",borderRadius:10,border:"none",background:(m==="Annual")===annual?G:"transparent",color:(m==="Annual")===annual?NAVY:"#6B7280",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:F,transition:"all 0.2s"}}>
              {m}{m==="Annual" ? " · save 20%" : ""}
            </button>
          ))}
        </div>
        {TIERS.map(t => {
          const active = currentTier === t.id;
          const price  = getPrice(t);
          return (
            <div key={t.id} style={{marginBottom:12,padding:"20px",borderRadius:16,border:"2px solid "+(active?G:t.popular?"#E5E7EB":"#F3F4F6"),background:"#fff",boxShadow:t.popular?"0 4px 20px rgba(34,197,94,0.12)":"0 1px 6px rgba(0,0,0,0.05)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                    <span style={{fontSize:20,fontWeight:800,color:NAVY,fontFamily:F}}>{t.name}</span>
                    {t.popular && <Pill label="Most Popular"/>}
                    {active    && <Pill label="Current" color={G} bg={GLt}/>}
                  </div>
                  <div style={{fontSize:13,color:"#6B7280",fontFamily:F}}>{t.desc}</div>
                </div>
                <div style={{textAlign:"right",paddingLeft:10,flexShrink:0}}>
                  <div style={{fontSize:26,fontWeight:800,color:NAVY,fontFamily:F,letterSpacing:"-0.5px"}}>{price}</div>
                  {t.price !== "Free" && <div style={{fontSize:11,color:"#9CA3AF",fontFamily:F,marginTop:2}}>/month{annual?" · billed annually":""}</div>}
                </div>
              </div>
              <div style={{borderTop:"1px solid #F0F2F0",paddingTop:14,marginBottom:16}}>
                {t.highlights.map((h,i) => (
                  <div key={i} style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:i<t.highlights.length-1?10:0}}>
                    <div style={{width:18,height:18,borderRadius:"50%",background:active?G:GLt,flexShrink:0,marginTop:1,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <Icon name="check" size={11} color={active?NAVY:G}/>
                    </div>
                    <span style={{fontSize:13,color:"#6B7280",fontFamily:F,lineHeight:1.5}}>{h}</span>
                  </div>
                ))}
              </div>
              {active ? (
                <GreenBtn label="Current plan" onPress={()=>{}} style={{opacity:0.6,cursor:"default"}}/>
              ) : t.price === "Free" ? (
                <OutlineBtn label="Select Scout" onPress={()=>onSelect(t.id)}/>
              ) : (
                <GreenBtn label={"Get Elite — $3.99/mo"} onPress={()=>{ setChosen(t); setStep("payment"); }}/>
              )}
            </div>
          );
        })}
        <p style={{fontSize:12,color:"#9CA3AF",textAlign:"center",fontFamily:F,lineHeight:1.7,marginTop:8}}>Predictions are for informational purposes only. Please bet responsibly.</p>
      </div>
    </div>
  );

  // ── Step 2: Payment form ──
  if (step === "payment") {
    const price = getPrice(chosen);
    return (
      <div style={{paddingBottom:88}}>
        <PageHeader title="Payment" onBack={()=>setStep("plans")}/>
        <div style={{padding:"20px 18px 0"}}>

          {/* Order summary */}
          <div style={{background:GLt,borderRadius:14,padding:"16px 18px",marginBottom:24,border:"1px solid rgba(34,197,94,0.2)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <span style={{fontSize:15,fontWeight:700,color:NAVY,fontFamily:F}}>{chosen.name} Plan</span>
              <span style={{fontSize:20,fontWeight:800,color:G,fontFamily:F}}>{price}<span style={{fontSize:13,color:"#6B7280",fontWeight:400}}>/mo</span></span>
            </div>
            <div style={{fontSize:13,color:"#6B7280",fontFamily:F,marginBottom:12}}>{chosen.desc}</div>
            <div style={{borderTop:"1px solid rgba(34,197,94,0.2)",paddingTop:12}}>
              {chosen.highlights.slice(0,3).map((h,i) => (
                <div key={i} style={{display:"flex",alignItems:"center",gap:8,marginBottom:i<2?6:0}}>
                  <Icon name="check" size={14} color={G}/>
                  <span style={{fontSize:12,color:NAVY,fontFamily:F}}>{h}</span>
                </div>
              ))}
              {chosen.highlights.length > 3 && <div style={{fontSize:12,color:G,fontFamily:F,marginTop:6,fontWeight:600}}>+{chosen.highlights.length-3} more features included</div>}
            </div>
          </div>

          {/* Card form */}
          <div style={{fontSize:15,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:16}}>Card Details</div>

          {/* Card number with brand icons */}
          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.07em",fontFamily:F,marginBottom:6}}>CARD NUMBER</div>
            <div style={{position:"relative"}}>
              <input value={card.number} onChange={e=>setCard({...card,number:fmtCard(e.target.value)})} placeholder="1234 5678 9012 3456" maxLength={19}
                style={{width:"100%",padding:"13px 14px",paddingRight:100,borderRadius:12,border:"1.5px solid "+(errors.number?"#EF4444":"#E5E7EB"),fontSize:15,color:NAVY,fontFamily:F,outline:"none",background:"#fff",boxSizing:"border-box"}}
                onFocus={e=>e.target.style.border="1.5px solid "+G}
                onBlur={e=>e.target.style.border="1.5px solid "+(errors.number?"#EF4444":"#E5E7EB")}
              />
              <div style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",display:"flex",gap:6}}>
                {["VISA","MC","AMEX"].map(b => (
                  <div key={b} style={{fontSize:9,fontWeight:800,color:"#9CA3AF",background:"#F3F4F6",borderRadius:4,padding:"2px 5px",fontFamily:F}}>{b}</div>
                ))}
              </div>
            </div>
            {errors.number && <div style={{fontSize:12,color:"#EF4444",fontFamily:F,marginTop:4}}>{errors.number}</div>}
          </div>

          <div style={{display:"flex",gap:"4%"}}>
            <div style={{width:"48%",marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.07em",fontFamily:F,marginBottom:6}}>EXPIRY DATE</div>
              <input value={card.expiry} onChange={e=>setCard({...card,expiry:fmtExpiry(e.target.value)})} placeholder="MM / YY" maxLength={5}
                style={{width:"100%",padding:"13px 14px",borderRadius:12,border:"1.5px solid "+(errors.expiry?"#EF4444":"#E5E7EB"),fontSize:15,color:NAVY,fontFamily:F,outline:"none",background:"#fff",boxSizing:"border-box"}}
                onFocus={e=>e.target.style.border="1.5px solid "+G}
                onBlur={e=>e.target.style.border="1.5px solid "+(errors.expiry?"#EF4444":"#E5E7EB")}
              />
              {errors.expiry && <div style={{fontSize:12,color:"#EF4444",fontFamily:F,marginTop:4}}>{errors.expiry}</div>}
            </div>
            <div style={{width:"48%",marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.07em",fontFamily:F,marginBottom:6}}>CVV</div>
              <input value={card.cvv} onChange={e=>setCard({...card,cvv:fmtCvv(e.target.value)})} placeholder="•••" maxLength={4} type="password"
                style={{width:"100%",padding:"13px 14px",borderRadius:12,border:"1.5px solid "+(errors.cvv?"#EF4444":"#E5E7EB"),fontSize:15,color:NAVY,fontFamily:F,outline:"none",background:"#fff",boxSizing:"border-box"}}
                onFocus={e=>e.target.style.border="1.5px solid "+G}
                onBlur={e=>e.target.style.border="1.5px solid "+(errors.cvv?"#EF4444":"#E5E7EB")}
              />
              {errors.cvv && <div style={{fontSize:12,color:"#EF4444",fontFamily:F,marginTop:4}}>{errors.cvv}</div>}
            </div>
          </div>

          <div style={{marginBottom:24}}>
            <div style={{fontSize:11,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.07em",fontFamily:F,marginBottom:6}}>CARDHOLDER NAME</div>
            <input value={card.name} onChange={e=>setCard({...card,name:e.target.value})} placeholder="Name on card"
              style={{width:"100%",padding:"13px 14px",borderRadius:12,border:"1.5px solid "+(errors.name?"#EF4444":"#E5E7EB"),fontSize:15,color:NAVY,fontFamily:F,outline:"none",background:"#fff",boxSizing:"border-box"}}
              onFocus={e=>e.target.style.border="1.5px solid "+G}
              onBlur={e=>e.target.style.border="1.5px solid "+(errors.name?"#EF4444":"#E5E7EB")}
            />
            {errors.name && <div style={{fontSize:12,color:"#EF4444",fontFamily:F,marginTop:4}}>{errors.name}</div>}
          </div>

          {/* Security badge */}
          <div style={{display:"flex",alignItems:"center",gap:10,background:"#F9FAFB",borderRadius:12,padding:"12px 16px",marginBottom:20}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2L4 6v6c0 5.5 3.5 10.7 8 12 4.5-1.3 8-6.5 8-12V6L12 2z" stroke={G} strokeWidth="2" fill="none" strokeLinejoin="round"/><path d="M9 12l2 2 4-4" stroke={G} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:NAVY,fontFamily:F}}>256-bit SSL encryption</div>
              <div style={{fontSize:11,color:"#9CA3AF",fontFamily:F}}>Your payment info is secure and never stored</div>
            </div>
          </div>

          <GreenBtn
            label={loading ? "Processing…" : "Pay "+price+" / month"}
            onPress={handlePay}
            style={{opacity:loading?0.7:1,cursor:loading?"not-allowed":"pointer",marginBottom:12}}
          />
          <p style={{fontSize:12,color:"#9CA3AF",textAlign:"center",fontFamily:F,lineHeight:1.6}}>By subscribing you agree to our Terms. Cancel anytime from your profile.</p>
        </div>
      </div>
    );
  }

  // ── Step 3: Success ──
  if (step === "success") return (
    <div style={{minHeight:"100vh",background:"#fff",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"0 24px",textAlign:"center"}}>
      <div style={{width:80,height:80,borderRadius:40,background:GLt,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:24}}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill={G}/>
          <path d="M8 12l3 3 5-5" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <div style={{fontSize:28,fontWeight:800,color:NAVY,fontFamily:F,marginBottom:8,letterSpacing:"-0.3px"}}>You're on {chosen.name}!</div>
      <div style={{fontSize:15,color:"#6B7280",fontFamily:F,lineHeight:1.7,marginBottom:32}}>Payment confirmed. All {chosen.name} features are now unlocked and ready to use.</div>

      <div style={{width:"100%",background:"#F9FAFB",borderRadius:16,padding:"20px",marginBottom:32,textAlign:"left"}}>
        <div style={{fontSize:12,fontWeight:700,color:G,letterSpacing:"0.08em",fontFamily:F,marginBottom:14}}>UNLOCKED FOR YOU</div>
        {chosen.highlights.map((h,i) => (
          <div key={i} style={{display:"flex",alignItems:"center",gap:12,marginBottom:i<chosen.highlights.length-1?12:0}}>
            <div style={{width:22,height:22,borderRadius:11,background:G,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Icon name="check" size={13} color="#fff"/>
            </div>
            <span style={{fontSize:14,color:NAVY,fontFamily:F,fontWeight:500}}>{h}</span>
          </div>
        ))}
      </div>

      <GreenBtn label="Start Exploring →" onPress={onBack} style={{marginBottom:0}}/>
    </div>
  );

  return null;
};

// ── Leaderboard ────────────────────────────────────────────
const LeaderboardScreen = ({ onBack, liveLeaderboard, currentUser }) => {
  const board = liveLeaderboard && liveLeaderboard.length > 0 ? liveLeaderboard : [];
  const top3 = board.slice(0,3);
  const meRow = board.find(u => u.user_id === currentUser?.id);
  const meRank = meRow ? board.indexOf(meRow)+1 : null;

  return (
  <div style={{paddingBottom:80}}>
    <PageHeader title="Leaderboard" onBack={onBack}/>
    <div style={{padding:"0 18px"}}>

      <div style={{background:"#F9FAFB",borderRadius:14,padding:"14px 16px",marginBottom:16,display:"flex",gap:12,alignItems:"flex-start"}}>
        <span style={{fontSize:20,flexShrink:0}}>🏆</span>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:3}}>Ranked by Prediction Accuracy</div>
          <div style={{fontSize:12,color:"#6B7280",fontFamily:F,lineHeight:1.6}}>Track picks on any prediction — when the match settles, your hit rate updates automatically. No money involved.</div>
        </div>
      </div>

      {board.length === 0 ? (
        <div style={{textAlign:"center",padding:"60px 24px"}}>
          <div style={{fontSize:40,marginBottom:12}}>🏅</div>
          <div style={{fontSize:16,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:8}}>Leaderboard is warming up</div>
          <div style={{fontSize:13,color:"#6B7280",fontFamily:F,lineHeight:1.6}}>Start tracking picks from the Feed. Once predictions settle, rankings appear here automatically.</div>
        </div>
      ) : (
        <>
          {top3.length >= 3 && (
            <div style={{display:"flex",alignItems:"flex-end",gap:8,marginBottom:16,justifyContent:"center"}}>
              {[top3[1], top3[0], top3[2]].map((u,i) => {
                const heights = [90,110,80];
                const medals = ["🥈","🥇","🥉"];
                const colors = ["#9CA3AF", G, AMBER];
                const acc = u.accuracy_pct ?? u.accuracy ?? 0;
                const picks = u.total_tracked ?? u.tracked ?? 0;
                const name = u.full_name || u.username || ("User "+String(u.user_id||"").slice(0,6));
                return (
                  <div key={i} style={{flex:1,textAlign:"center"}}>
                    <div style={{fontSize:11,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:4,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{name}</div>
                    <div style={{fontSize:13,fontWeight:700,color:colors[i],fontFamily:F,marginBottom:6}}>{acc}%</div>
                    <div style={{height:heights[i],borderRadius:"10px 10px 0 0",background:colors[i]+"22",border:"2px solid "+colors[i],display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-end",paddingBottom:8}}>
                      <span style={{fontSize:22}}>{medals[i]}</span>
                      <div style={{fontSize:11,color:colors[i],fontFamily:F,fontWeight:700,marginTop:2}}>{picks} picks</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {meRow && (
            <Card style={{padding:"16px 18px",marginBottom:16,background:GLt,border:"1px solid "+G+"44"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:G,letterSpacing:"0.07em",fontFamily:F,marginBottom:4}}>YOUR RANKING</div>
                  <div style={{fontSize:28,fontWeight:800,color:NAVY,fontFamily:F}}>#{meRank} <span style={{fontSize:14,color:"#9CA3AF",fontWeight:500}}>of {board.length}</span></div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:11,color:"#9CA3AF",fontFamily:F,marginBottom:3}}>Accuracy</div>
                  <div style={{fontSize:24,fontWeight:800,color:G,fontFamily:F}}>{meRow.accuracy_pct ?? meRow.accuracy ?? 0}%</div>
                </div>
              </div>
            </Card>
          )}

          <Card style={{marginBottom:16}}>
            <div style={{display:"grid",gridTemplateColumns:"28px 1fr 60px 60px",gap:4,padding:"10px 18px",borderBottom:"1px solid #F0F2F0"}}>
              {["#","Player","Picks","Hit %"].map((h,i) => (
                <div key={i} style={{fontSize:10,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.06em",fontFamily:F,textAlign:i>1?"center":"left"}}>{h}</div>
              ))}
            </div>
            {board.map((u,i) => {
              const isMe = u.user_id === currentUser?.id;
              const acc = u.accuracy_pct ?? u.accuracy ?? 0;
              const picks = u.total_tracked ?? u.tracked ?? 0;
              const name = u.full_name || u.username || ("User "+String(u.user_id||"").slice(0,6));
              return (
                <div key={i}>
                  <div style={{display:"grid",gridTemplateColumns:"28px 1fr 60px 60px",gap:4,alignItems:"center",padding:"12px 18px",background:isMe?GLt:"transparent"}}>
                    <span style={{fontSize:13,fontWeight:700,color:i<3?G:"#9CA3AF",fontFamily:F}}>#{i+1}</span>
                    <div>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <span style={{fontSize:14,fontWeight:isMe?800:600,color:NAVY,fontFamily:F}}>{name}</span>
                        {isMe && <Pill label="You"/>}
                      </div>
                      <div style={{fontSize:11,color:"#9CA3AF",fontFamily:F,marginTop:1}}>{u.tier || "Scout"}</div>
                    </div>
                    <div style={{textAlign:"center",fontSize:13,color:"#6B7280",fontFamily:F}}>{picks}</div>
                    <div style={{textAlign:"center"}}><span style={{fontSize:14,fontWeight:700,color:acc>=80?G:acc>=70?AMBER:"#9CA3AF",fontFamily:F}}>{acc}%</span></div>
                  </div>
                  {i<board.length-1 && <Divider inset/>}
                </div>
              );
            })}
          </Card>
        </>
      )}

      <div style={{background:"#F9FAFB",borderRadius:12,padding:"12px 16px",marginBottom:16}}>
        <div style={{fontSize:12,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:4}}>How accuracy is calculated</div>
        <div style={{fontSize:12,color:"#6B7280",fontFamily:F,lineHeight:1.7}}>When you tap "Track Pick" on any prediction, it's added to your record. Once the match settles, we mark it correct or incorrect. Your accuracy = correct picks ÷ total settled picks.</div>
      </div>
    </div>
  </div>
  );
}

// ── Profile ────────────────────────────────────────────────
const BillingScreen = ({ tier, onBack, onUpgrade }) => (
  <div style={{paddingBottom:88}}>
    <PageHeader title="Billing & Payments" onBack={onBack}/>
    <div style={{padding:"16px 18px"}}>
      {tier === "scout" ? (
        <div style={{textAlign:"center",padding:"40px 0"}}>
          <div style={{fontSize:48,marginBottom:16}}>💳</div>
          <div style={{fontSize:18,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:8}}>No active subscription</div>
          <div style={{fontSize:14,color:"#6B7280",fontFamily:F,lineHeight:1.6,marginBottom:24}}>You're on the free Scout plan. Upgrade to Elite to access all features.</div>
          <GreenBtn label="Upgrade to Elite — $3.99/mo" onPress={onUpgrade}/>
        </div>
      ) : (
        <div>
          <Card style={{padding:"18px",marginBottom:16,border:"1px solid "+G+"33"}}>
            <div style={{fontSize:11,fontWeight:700,color:G,letterSpacing:"0.08em",fontFamily:F,marginBottom:12}}>ACTIVE SUBSCRIPTION</div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div>
                <div style={{fontSize:18,fontWeight:800,color:NAVY,fontFamily:F}}>Elite Plan</div>
                <div style={{fontSize:13,color:"#6B7280",fontFamily:F,marginTop:2}}>Renews on 7 April 2026</div>
              </div>
              <div style={{fontSize:22,fontWeight:800,color:G,fontFamily:F}}>$3.99<span style={{fontSize:13,color:"#9CA3AF",fontWeight:400}}>/mo</span></div>
            </div>
            <div style={{background:"#F9FAFB",borderRadius:10,padding:"12px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:12,color:"#9CA3AF",fontFamily:F}}>Next charge</div>
                <div style={{fontSize:14,fontWeight:700,color:NAVY,fontFamily:F}}>$3.99 on 7 Apr 2026</div>
              </div>
              <div style={{width:10,height:10,borderRadius:5,background:G}}/>
            </div>
          </Card>
          <Card style={{padding:"18px",marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.08em",fontFamily:F,marginBottom:14}}>PAYMENT METHOD</div>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
              <div style={{width:48,height:32,borderRadius:6,background:"#1A1F71",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <span style={{fontSize:11,fontWeight:800,color:"#fff",fontFamily:F,letterSpacing:"0.05em"}}>VISA</span>
              </div>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:NAVY,fontFamily:F}}>•••• •••• •••• 4242</div>
                <div style={{fontSize:12,color:"#9CA3AF",fontFamily:F}}>Expires 09/27</div>
              </div>
              <div style={{marginLeft:"auto"}}>
                <span style={{fontSize:12,fontWeight:700,color:G,fontFamily:F,cursor:"pointer"}}>Change</span>
              </div>
            </div>
          </Card>
          <Card style={{padding:"18px",marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.08em",fontFamily:F,marginBottom:14}}>BILLING HISTORY</div>
            {[
              {date:"7 Mar 2026",amount:"$3.99",status:"Paid"},
              {date:"7 Feb 2026",amount:"$3.99",status:"Paid"},
              {date:"7 Jan 2026",amount:"$3.99",status:"Paid"},
            ].map((tx,i,arr) => (
              <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:i<arr.length-1?"1px solid #F0F2F0":"none"}}>
                <div>
                  <div style={{fontSize:14,color:NAVY,fontFamily:F}}>{tx.date}</div>
                  <div style={{fontSize:12,color:"#9CA3AF",fontFamily:F}}>Elite Monthly</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:14,fontWeight:700,color:NAVY,fontFamily:F}}>{tx.amount}</div>
                  <div style={{fontSize:12,fontWeight:600,color:G,fontFamily:F}}>{tx.status}</div>
                </div>
              </div>
            ))}
          </Card>
          <button style={{width:"100%",padding:"14px 0",borderRadius:14,border:"1.5px solid #EF4444",background:"#fff",color:"#EF4444",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:F}}>
            Cancel Subscription
          </button>
          <p style={{fontSize:12,color:"#9CA3AF",textAlign:"center",fontFamily:F,marginTop:12,lineHeight:1.6}}>Cancellations take effect at the end of the billing period.</p>
        </div>
      )}
    </div>
  </div>
);

const TermsScreen = ({ onBack }) => (
  <div style={{paddingBottom:88}}>
    <PageHeader title="Terms & Privacy" onBack={onBack}/>
    <div style={{padding:"16px 18px"}}>
      {[
        {title:"Terms of Service", body:"SportOracle provides AI-generated sports predictions for informational purposes only. By using this app you agree that predictions do not constitute financial or betting advice. Past performance is not indicative of future results. You must be 18+ and in a jurisdiction where sports betting is legal to use this service."},
        {title:"Subscription & Billing", body:"Elite plan is billed at $3.99/month. You may cancel at any time from the Billing & Payments screen. Cancellations take effect at the end of the current billing period. Refunds are not issued for partial months."},
        {title:"Data & Privacy", body:"We collect account data (email, phone) and usage data to personalise predictions. We never sell your personal data to third parties. Notification data (push, WhatsApp, email) is processed only with your explicit opt-in consent and may be withdrawn at any time in Profile settings."},
        {title:"Responsible Gambling", body:"SportOracle is committed to responsible gambling. If you or someone you know has a gambling problem, please contact the National Gambling Helpline: 0808 8020 133 (UK) or visit begambleaware.org. Never bet more than you can afford to lose."},
        {title:"Disclaimer", body:"All predictions are generated by machine learning models and are not guaranteed to be correct. SportOracle accepts no liability for financial losses arising from the use of predictions on this platform."},
        {title:"Contact", body:"For support or legal enquiries: support@sportoracle.app. For data requests: privacy@sportoracle.app."},
      ].map((s,i) => (
        <div key={i} style={{marginBottom:20}}>
          <div style={{fontSize:15,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:8}}>{s.title}</div>
          <div style={{fontSize:13,color:"#6B7280",fontFamily:F,lineHeight:1.8}}>{s.body}</div>
          {i < 5 && <div style={{height:1,background:"#F0F2F0",marginTop:20}}/>}
        </div>
      ))}
      <div style={{fontSize:12,color:"#9CA3AF",textAlign:"center",fontFamily:F,marginTop:8}}>Last updated: March 2026 · v2.1.0</div>
    </div>
  </div>
);

const ProfileScreen = ({ tier, onNav, onUpgrade, user, onLogout }) => {
  const [alerts, setAlerts] = useState({email: tierGte(tier,"elite"), push: tierGte(tier,"elite"), whatsapp: tierGte(tier,"elite")});
  const [subScreen, setSubScreen] = useState(null);
  const toggleAlert = (key) => {
    if (!tierGte(tier,"elite")) { onUpgrade(); return; }
    setAlerts(a => ({...a, [key]: !a[key]}));
  };
  const handleLogout = () => onLogout ? onLogout() : window.location.reload();

  if (subScreen === "billing") return <BillingScreen tier={tier} onBack={()=>setSubScreen(null)} onUpgrade={()=>{setSubScreen(null);onUpgrade();}}/>;
  if (subScreen === "terms")   return <TermsScreen onBack={()=>setSubScreen(null)}/>;

  return (
  <div style={{padding:"16px 18px 100px",overflowY:"auto",minHeight:"100vh"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
      <span style={{fontSize:20,fontWeight:800,color:NAVY,fontFamily:FH,letterSpacing:"-0.3px"}}>Profile</span>
      <LogoMark size={28}/>
    </div>

    <Card style={{padding:"16px 18px",marginBottom:20,display:"flex",alignItems:"center",gap:14}}>
      <div style={{width:52,height:52,borderRadius:26,background:G,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <span style={{fontSize:20,fontWeight:800,color:"#fff",fontFamily:F}}>JB</span>
      </div>
      <div style={{flex:1}}>
        <div style={{fontSize:17,fontWeight:800,color:NAVY,fontFamily:F}}>{user?.user_metadata?.full_name || user?.email?.split("@")[0] || "User"}</div>
        <div style={{fontSize:13,color:"#6B7280",fontFamily:F,marginTop:2}}>{user?.email || ""}</div>
      </div>
      <Pill label={tier.toUpperCase()}/>
    </Card>

    {tierGte(tier,"elite") && (
      <Card style={{padding:"18px",marginBottom:20,border:"1px solid "+G+"33"}}>
        <div style={{fontSize:11,fontWeight:700,color:G,letterSpacing:"0.08em",fontFamily:F,marginBottom:12}}>DEDICATED ACCOUNT MANAGER</div>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
          <div style={{width:44,height:44,borderRadius:22,background:GLt,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <span style={{fontSize:16,fontWeight:800,color:G,fontFamily:F}}>SC</span>
          </div>
          <div>
            <div style={{fontSize:15,fontWeight:700,color:NAVY,fontFamily:F}}>Sarah Chen</div>
            <div style={{fontSize:12,color:"#6B7280",fontFamily:F}}>Elite Account Manager</div>
          </div>
        </div>
        <div style={{display:"flex",gap:10}}>
          <GreenBtn label="Email" onPress={()=>window.open("mailto:sarah.chen@sportoracle.app","_blank")} style={{fontSize:13}}/>
          <GreenBtn label="WhatsApp" onPress={()=>window.open("https://wa.me/447700900123","_blank")} style={{fontSize:13,background:"#25D366",color:"#fff"}}/>
        </div>
      </Card>
    )}

    <div style={{marginBottom:20}}>
      <div style={{fontSize:12,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.08em",fontFamily:F,marginBottom:10}}>ALERTS</div>
      <Card>
        {[
          {key:"email",    label:"Email alerts",       sub:"Daily digest"},
          {key:"push",     label:"Push notifications", sub:"Live picks"},
          {key:"whatsapp", label:"WhatsApp alerts",    sub:"Instant updates"},
        ].map((item,i,arr) => {
          const isOn = alerts[item.key];
          const canUse = tierGte(tier,"elite");
          return (
          <div key={i}>
            <div onClick={()=>toggleAlert(item.key)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 18px",cursor:"pointer"}}>
              <div>
                <div style={{fontSize:15,color:NAVY,fontFamily:F}}>{item.label}</div>
                <div style={{fontSize:12,color:"#9CA3AF",fontFamily:F,marginTop:2}}>{item.sub}</div>
              </div>
              {canUse
                ? <div style={{width:44,height:26,borderRadius:13,background:isOn?G:"#E5E7EB",display:"flex",alignItems:"center",justifyContent:isOn?"flex-end":"flex-start",padding:"0 3px",transition:"background 0.2s",cursor:"pointer"}}>
                    <div style={{width:20,height:20,borderRadius:10,background:"#fff",boxShadow:"0 1px 4px rgba(0,0,0,0.2)",transition:"all 0.2s"}}/>
                  </div>
                : <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <Icon name="lock" size={13} color="#9CA3AF"/>
                    <span onClick={(e)=>{e.stopPropagation();onUpgrade();}} style={{fontSize:11,fontWeight:700,color:G,background:GLt,borderRadius:6,padding:"3px 8px",fontFamily:F,cursor:"pointer"}}>ELITE+</span>
                  </div>
              }
            </div>
            {i<arr.length-1 && <Divider inset/>}
          </div>
          );
        })}
      </Card>
    </div>

    {[
      {title:"Account", items:[
        {label:"Plans & Pricing",    action:()=>onNav("plans")},
        {label:"Billing & Payments", action:()=>setSubScreen("billing")},
      ]},
      {title:"Tools", items:[
        {label:"Arbitrage Scanner",  action:()=>onNav("arb")},
        {label:"Leaderboard",        action:()=>onNav("leaderboard")},
        {label:"Value Bets",         action:()=>onNav("value")},
        {label:"Bankroll",           action:()=>onNav("bankroll")},
      ]},
      {title:"Support", items:[
        {label:tierGte(tier,"elite")?"Priority Support ⭐":"Help Centre", action:()=>window.open("mailto:support@sportoracle.app","_blank")},
        {label:"Contact Us",         action:()=>window.open("mailto:support@sportoracle.app","_blank")},
        {label:"Terms & Privacy",    action:()=>setSubScreen("terms")},
      {label:"Sign Out",             action:handleLogout, danger:true},
      ]},
    ].map((section,si) => (
      <div key={si} style={{marginBottom:20}}>
        <div style={{fontSize:12,fontWeight:700,color:"#9CA3AF",letterSpacing:"0.08em",fontFamily:F,marginBottom:10}}>{section.title.toUpperCase()}</div>
        <Card>
          {section.items.map((item,i,arr) => (
            <div key={i}>
              <div onClick={item.action} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"15px 18px",cursor:"pointer"}}>
                <span style={{fontSize:15,color:item.danger?"#EF4444":NAVY,fontFamily:F,fontWeight:item.danger?600:400}}>{item.label}</span>
                <span style={{color:"#C4CCC8",fontSize:20,lineHeight:1}}>›</span>
              </div>
              {i<arr.length-1 && <Divider inset/>}
            </div>
          ))}
        </Card>
      </div>
    ))}
  </div>
  );
}

// ── Picks Screen ──────────────────────────────────────────
// A compact list view of all predictions — different from the
// card-heavy Feed. Shows sport, match, prediction, EV and status.
const PicksScreen = ({ tier, onSelectPick, onUpgrade, picks: livePicks }) => {
  const [filter, setFilter] = useState("All");
  const filters = ["All","Open","Today","This Week"];

  const getStatus = (p) => {
    if (p.time.startsWith("Today"))    return "Open";
    if (p.time.startsWith("Tomorrow")) return "This Week";
    return "This Week";
  };

  const all = livePicks || PICKS;
  const filtered = all.filter(p => {
    if (filter === "All") return true;
    return getStatus(p) === filter || (filter === "Today" && p.time.startsWith("Today"));
  });

  const scoutSports = ["Football","Basketball"];

  return (
    <div style={{paddingBottom:80}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 18px 14px"}}>
        <span style={{fontSize:20,fontWeight:800,color:NAVY,fontFamily:F,letterSpacing:"-0.3px"}}>All Predictions</span>
        <div style={{background:GLt,borderRadius:8,padding:"4px 10px"}}>
          <span style={{fontSize:12,fontWeight:700,color:G,fontFamily:F}}>{filtered.length} picks</span>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{display:"flex",gap:8,paddingLeft:18,paddingBottom:14,overflowX:"auto",scrollbarWidth:"none"}}>
        {filters.map(f => (
          <button key={f} onClick={()=>setFilter(f)} style={{
            flexShrink:0, padding:"7px 16px", borderRadius:20,
            border:"none", cursor:"pointer", fontFamily:F, fontSize:13, fontWeight:600,
            background: filter===f ? G : "#F3F4F6",
            color: filter===f ? NAVY : "#6B7280",
            transition:"all 0.2s",
          }}>{f}</button>
        ))}
      </div>

      <div style={{padding:"0 18px"}}>
        {filtered.map((p,idx) => {
          const locked = !tierGte(tier, p.minTier);
          const isScoutSport = scoutSports.includes(p.sport);
          const scoutLocked = tier === "scout" && !isScoutSport;
          const actuallyLocked = locked || scoutLocked;
          const statusColor = p.time.startsWith("Today") ? G : "#6B7280";

          return (
            <div key={p.id} onClick={() => actuallyLocked ? onUpgrade() : onSelectPick(p)}
              style={{
                background:"#fff", borderRadius:14, marginBottom:10,
                boxShadow:"0 1px 6px rgba(0,0,0,0.06)",
                border: actuallyLocked ? "1px solid #F3F4F6" : "1px solid transparent",
                cursor:"pointer", overflow:"hidden",
                opacity: actuallyLocked ? 0.75 : 1,
              }}
            >
              <div style={{display:"flex",alignItems:"center",padding:"14px 16px",gap:12}}>
                {/* Sport icon pill */}
                <div style={{
                  width:42, height:42, borderRadius:12, flexShrink:0,
                  background: actuallyLocked ? "#F3F4F6" : GLt,
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  <span style={{fontSize:18}}>
                    {p.sport==="Football"?"⚽":p.sport==="Basketball"?"🏀":p.sport==="Tennis"?"🎾":p.sport==="Am. Football"?"🏈":"🏏"}
                  </span>
                </div>

                {/* Main info */}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                    <span style={{fontSize:11,fontWeight:700,color:actuallyLocked?"#9CA3AF":G,letterSpacing:"0.06em",fontFamily:F}}>{p.league}</span>
                    {p.early && tierGte(tier,"elite") && <Pill label="6H EARLY" color={NAVY} bg={AMBER} style={{fontSize:9}}/>}
                  </div>
                  <div style={{fontSize:14,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                    {p.home} vs {p.away}
                  </div>
                  <div style={{fontSize:12,color:"#9CA3AF",fontFamily:F}}>{p.time}</div>
                </div>

                {/* Right side */}
                {actuallyLocked ? (
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:4,justifyContent:"flex-end",marginBottom:4}}>
                      <Icon name="lock" size={12} color="#9CA3AF"/>
                      <span style={{fontSize:11,fontWeight:700,color:G,fontFamily:F}}>Elite</span>
                    </div>
                    <div style={{fontSize:11,color:"#9CA3AF",fontFamily:F}}>Upgrade →</div>
                  </div>
                ) : (
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontSize:15,fontWeight:800,color:NAVY,fontFamily:F,marginBottom:3}}>{p.prediction}</div>
                    <div style={{display:"flex",alignItems:"center",gap:6,justifyContent:"flex-end"}}>
                      <span style={{fontSize:12,fontWeight:700,color:G,fontFamily:F}}>{p.confidence}%</span>
                      <span style={{fontSize:11,color:"#9CA3AF",fontFamily:F}}>·</span>
                      <span style={{fontSize:12,fontWeight:700,color:G,fontFamily:F}}>{p.ev}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Bottom bar for open picks */}
              {!actuallyLocked && (
                <div style={{
                  background:"#F9FAFB", borderTop:"1px solid #F0F2F0",
                  padding:"8px 16px", display:"flex", justifyContent:"space-between", alignItems:"center",
                }}>
                  <div style={{display:"flex",gap:12}}>
                    <span style={{fontSize:11,color:"#9CA3AF",fontFamily:F}}>Odds <span style={{fontWeight:700,color:NAVY}}>{p.odds}</span></span>
                    {p.sharp && tierGte(tier,"elite") && <span style={{fontSize:11,color:AMBER,fontWeight:700,fontFamily:F}}>⚡ Sharp</span>}
                  </div>
                  <span style={{fontSize:11,fontWeight:700,color:statusColor,fontFamily:F,letterSpacing:"0.03em"}}>
                    {p.time.startsWith("Today") ? "● OPEN" : "◯ UPCOMING"}
                  </span>
                </div>
              )}
            </div>
          );
        })}

        {tier === "scout" && (
          <div style={{background:GLt,borderRadius:14,padding:"16px",marginTop:8,textAlign:"center"}}>
            <div style={{fontSize:14,fontWeight:700,color:NAVY,fontFamily:F,marginBottom:6}}>
              {filtered.filter(p => !["Football","Basketball"].includes(p.sport) || p.minTier==="elite").length} more picks with Elite
            </div>
            <div style={{fontSize:13,color:"#6B7280",fontFamily:F,marginBottom:14,lineHeight:1.5}}>
              Unlock all sports, unlimited predictions and full AI analysis
            </div>
            <GreenBtn label="Upgrade to Elite — $3.99/mo" onPress={onUpgrade}/>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Nav ────────────────────────────────────────────────────
const NAV_ITEMS = [
  { id:"feed",     label:"Feed",     icon:"home"    },
  { id:"picks",    label:"Picks",    icon:"picks"   },
  { id:"stats",    label:"Stats",    icon:"stats"   },
  { id:"bankroll", label:"Bankroll", icon:"bankroll"},
  { id:"profile",  label:"Profile",  icon:"profile" },
];

// ── App Root ───────────────────────────────────────────────
export default function App() {
  const [screen,    setScreen]    = useState("onboarding");
  const [tab,       setTab]       = useState("feed");
  const [tier,      setTier]      = useState("scout");
  const [pick,      setPick]      = useState(null);
  const [subScreen, setSubScreen] = useState(null);
  const [user,      setUser]      = useState(null);
  const [picks,     setPicks]     = useState(null); // null = loading
  const [trackedIds, setTrackedIds] = useState(new Set());
  const [showNotif, setShowNotif] = useState(false);
  const [liveLeaderboard, setLiveLeaderboard] = useState(null);

  // ── Auth listener ───────────────────────────────────────
  useEffect(() => {
    // Handle Stripe return
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    if (paymentStatus === 'success') {
      window.history.replaceState({}, '', window.location.pathname);
      // Re-load profile after short delay to let webhook process
      setTimeout(() => { if (window._soUserId) loadProfile(window._soUserId); }, 3000);
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        loadProfile(session.user.id);
        setScreen("app");
        if (paymentStatus === 'success') {
          setTimeout(() => setScreen('app'), 100);
        }
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
        loadProfile(session.user.id);
        setScreen("app");
      } else {
        setUser(null);
        setTier("scout");
        setScreen("onboarding");
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Load profile + tier ─────────────────────────────────
  const loadProfile = async (userId) => {
    window._soUserId = userId;
    const { data } = await getProfile(userId);
    if (data?.tier) setTier(data.tier);
    // Load bankroll from Supabase if available
    if (data?.bankroll) {
      try {
        const parsed = typeof data.bankroll === 'string' ? JSON.parse(data.bankroll) : data.bankroll;
        if (parsed?.initial) localStorage.setItem('so_bankroll', JSON.stringify(parsed));
      } catch {}
    }
    // Check active subscription
    const { data: sub } = await getSubscription(userId);
    if (sub?.status === 'active') setTier('elite');
  };

  // ── Load picks from Supabase ────────────────────────────
  const loadPicks = () => {
    getPicks().then(({ data, error }) => {
      if (error) console.error('Picks error:', error);
      if (data && data.length > 0) {
        const mapped = data.map(p => ({
          ...p,
          minTier: p.min_tier || 'scout',
          probH: p.prob_h || 60,
          probD: p.prob_d || 20,
          probA: p.prob_a || 20,
          kickoff: p.kickoff || null,
          score: p.score || null,       // real score e.g. "2-1"
          status: p.status || null,     // "live" | "finished" | null
          oddsComp: typeof p.odds_comp === 'string'
            ? JSON.parse(p.odds_comp)
            : (Array.isArray(p.odds_comp) ? p.odds_comp : []),
          analysis: (() => {
            const a = p.analysis;
            if (!a) return null;
            if (typeof a === 'object' && !Array.isArray(a) && (a.verdict || a.narrative)) return a;
            if (typeof a === 'string' && a.length > 2) { try { const p2=JSON.parse(a); if(p2.verdict||p2.narrative) return p2; } catch {} }
            return null;
          })(),
          keyFactors: p.key_factors || [],
          kelly: p.kelly || null,
        }));
        console.log('Loaded', mapped.length, 'picks from Supabase ✅');
        setPicks(mapped);
      } else {
        console.log('Using built-in picks (Supabase empty or error)');
        setPicks(PICKS);
      }
    }).catch(() => setPicks(PICKS));
  };

  useEffect(() => {
    if (screen === "app") {
      loadPicks();
      getLeaderboard().then(({ data }) => {
        if (data && data.length > 0) setLiveLeaderboard(data);
      });
      // Poll every 60s to catch live score updates from update-scores function
      const interval = setInterval(loadPicks, 60000);
      return () => clearInterval(interval);
    }
  }, [screen]);

  // ── Handlers ────────────────────────────────────────────
  const handleAuth = (authUser) => {
    setUser(authUser);
    loadProfile(authUser.id);
    setScreen("app");
  };

  const handleTrackPick = async (pickId) => {
    if (!user) { alert("Please sign in to track picks."); return; }
    const isTracked = trackedIds.has(pickId);
    if (isTracked) {
      const { error } = await untrackPick(user.id, pickId);
      if (!error) setTrackedIds(prev => { const s = new Set(prev); s.delete(pickId); return s; });
    } else {
      const { error } = await trackPick(user.id, pickId);
      if (error && !error.message.includes('duplicate')) {
        console.error("Track error:", error);
      } else {
        setTrackedIds(prev => new Set([...prev, pickId]));
      }
    }
  };

  const handleLogout = async () => {
    if (window.confirm("Sign out of SportOracle?")) {
      await supabaseSignOut();
      window._soUserId = null; setUser(null); setTier("scout"); setPicks(null);
      setScreen("onboarding");
    }
  };

  const handleUpgradeSuccess = async (newTier) => {
    setTier(newTier);
    setSubScreen(null);
    if (user) {
      await upsertProfile(user.id, { tier: newTier });
    }
  };

  if (screen === "onboarding") return <OnboardingScreen onDone={()=>setScreen("auth")}/>;
  if (screen === "auth")       return <AuthScreen onAuth={handleAuth}/>;

  const go      = t => { setSubScreen(null); setPick(null); setTab(t); };
  const goSub   = s => { setPick(null); setSubScreen(s); };
  const upgrade = () => goSub("plans");

  const activePicks = picks || PICKS; // use live or fallback

  const renderContent = () => {
    if (pick)                        return <PickDetailScreen pick={pick} tier={tier} onBack={()=>setPick(null)} onUpgrade={upgrade} onTrack={handleTrackPick} tracked={trackedIds&&trackedIds.has(pick?.id)}/>;
    if (subScreen === "plans")       return <PlansScreen currentTier={tier} onSelect={handleUpgradeSuccess} onBack={()=>setSubScreen(null)} user={user}/>;
    if (subScreen === "value")       return <ValueScreen tier={tier} onBack={()=>setSubScreen(null)} onUpgrade={upgrade} liveValuePicks={(picks||[]).filter(p=>p.ev&&parseFloat(p.ev)>0)}/>;
    if (subScreen === "arb")         return <ArbScreen tier={tier} onBack={()=>setSubScreen(null)} onUpgrade={upgrade}/>;
    if (subScreen === "leaderboard") return <LeaderboardScreen onBack={()=>setSubScreen(null)} liveLeaderboard={liveLeaderboard} currentUser={user}/>;
    if (subScreen === "bankroll")    return <BankrollScreen tier={tier} onBack={()=>setSubScreen(null)} onUpgrade={upgrade}/>;
    switch(tab) {
      case "feed":     return <FeedScreen tier={tier} onSelectPick={setPick} onUpgrade={upgrade} picks={activePicks} loading={picks === null} onTrack={handleTrackPick} trackedIds={trackedIds} onNotif={()=>setShowNotif(true)} unreadCount={(activePicks||[]).length}/>;
      case "picks":    return <PicksScreen tier={tier} onSelectPick={setPick} onUpgrade={upgrade} picks={activePicks}/>;
      case "stats":    return <StatsScreen tier={tier} onUpgrade={upgrade} user={user}/>;
      case "bankroll": return <BankrollScreen tier={tier} onBack={null} onUpgrade={upgrade}/>;
      case "profile":  return <ProfileScreen tier={tier} onNav={goSub} onUpgrade={upgrade} user={user} onLogout={handleLogout}/>;
      default:         return <FeedScreen tier={tier} onSelectPick={setPick} onUpgrade={upgrade} picks={activePicks} loading={picks === null} onTrack={handleTrackPick}/>;
    }
  };

  return (
    <div style={{background:BG,minHeight:"100vh",maxWidth:430,margin:"0 auto"}}>
      <div style={{overflowY:"auto",height:"calc(100vh - 64px)",WebkitOverflowScrolling:"touch"}}>
        {renderContent()}
      </div>
      <div style={{position:"fixed",bottom:0,left:"50%",transform:"translateX(-50%)",width:"100%",maxWidth:430,background:"#fff",borderTop:"1px solid #E9EEF0",display:"flex",height:64,zIndex:100}}>
        {NAV_ITEMS.map(n => {
          const active = !pick && !subScreen && tab === n.id;
          const col = active ? G : "#9CA3AF";
          return (
            <button key={n.id} onClick={()=>go(n.id)} style={{flex:1,border:"none",background:"none",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4,cursor:"pointer"}}>
              <Icon name={n.icon} size={22} color={col}/>
              <span style={{fontSize:10,fontWeight:active?700:500,color:col,fontFamily:F}}>{n.label}</span>
            </button>
          );
        })}
      </div>
      {showNotif && (
        <NotificationsPanel
          onClose={()=>setShowNotif(false)}
          picks={activePicks||[]}
          trackedIds={trackedIds}
          user={user}
        />
      )}
    </div>
  );
}
